import React, { useState, useEffect, useCallback, useRef } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HelpModal from "./components/HelpModal";
import Greeting from "./components/Greeting";
import PanelRail from "./components/PanelRail";
import CalendarPanel from "./components/CalendarPanel";
import CollapsiblePanel from "./components/CollapsiblePanel";
import StickyNotesBoard from "./components/StickyNotesBoard";
import { datedNoteTasks, toggleCheckboxLine } from "./utils/noteMarkdown";
import {
  BOARD_CARD_W,
  BOARD_CARD_H,
  BOARD_GAP,
  boardColumns as boardColumnCount,
  boardMaxNoteW,
  snapToBoard,
} from "./components/StickyNotes";
import { StickyNotesLayer, StickyNotesPanel } from "./components/StickyNotes";
import CommandPalette from "./components/CommandPalette";
import UndoToast from "./components/UndoToast";
import Celebration from "./components/Celebration";
import StreakBanner from "./components/StreakBanner";
import FocusBar from "./components/FocusBar";
import Pomodoro from "./components/Pomodoro";
import WeeklyReview from "./components/WeeklyReview";
import {
  buildMarkdown,
  buildCsv,
  downloadBlob,
  renderShareCard,
} from "./utils/exporters";
import {
  Target as IcTarget,
  CheckCircle2 as IcCheck,
  StickyNote as IcStickyNote,
  Focus as IcFocus,
  CalendarDays as IcCalendarDays,
  Settings as IcSettings,
  Monitor as IcMonitor,
  Sun as IcSun,
  Moon as IcMoon,
  Palette as IcPalette,
  Sparkles as IcSparkles,
  Download as IcDownload,
  Share2 as IcShare,
  Navigation as IcNavigation,
  Clipboard as IcClipboard,
  GraduationCap as IcGraduationCap,
  BookOpen as IcBookOpen,
  PanelLeft as IcPanelLeft,
  PanelRight as IcPanelRight,
  Bell as IcBell,
  LayoutGrid as IcLayoutGrid,
  RotateCcw as IcRotateCcw,
} from "lucide-react";
import ProgressBar from "./components/ProgressBar";
import GoalsList from "./components/GoalsList";
import Reminders from "./components/Reminders";
import ImportantTasks from "./components/ImportantTasks";
import Clipboard from "./components/Clipboard";
import QuickLinks from "./components/QuickLinks";
import Conferences from "./components/Conferences";
import Publications from "./components/Publications";
import Remember from "./components/Remember";
import RememberPromptModal from "./components/RememberPromptModal";
import SettingsModal from "./components/SettingsModal";
import LoginModal from "./components/LoginModal";
import SyncStatusIndicator from "./components/SyncStatusIndicator";
import ConflictResolutionModal from "./components/ConflictResolutionModal";
import InstallPWA from "./components/InstallPWA";
import CookieConsent from "./components/CookieConsent";
import LegalModal from "./components/LegalModal";
import {
  generateChallengeDates,
  formatDate,
  calculateStreak,
  calculateBestStreak,
} from "./utils/dateUtils";
import { getNextOccurrence, isRecurring } from "./utils/recurrence";
import { resolvePublication } from "./utils/publications";
import {
  conferenceReminderItems,
  conferenceRememberItems,
} from "./utils/conferences";
import {
  initAnalytics,
  analyticsAvailable,
  trackLogin,
  trackGoalCompleted,
  trackRememberAdded,
  trackConferenceAdded,
  trackPanelReorder,
} from "./utils/analytics";
import {
  fetchAllData,
  saveAllData,
  debounce,
  migrateFromLocalStorage,
  mergeDataWithSelections,
  getCurrentTimestamp,
  cleanupDuplicates,
  // record-level sync
  getDeviceId,
  newRecordId,
  setSyncCursor,
  clearSyncCursor,
  readOutbox,
  writeOutbox,
  enqueueChanges,
  diffStates,
  buildBootstrapEntries,
  buildLocalOnlyEntries,
  flushOutbox,
  fetchChangesSince,
  isNewer,
  migrateSyncStateIfNeeded,
  clearSyncState,
  outboxCount,
  cloudLogin,
  cloudMe,
  cloudLogout,
  isUiOnlyChange,
  resetPasskeyWithRecovery,
} from "./services/api";
import { sortByOrder, withSequentialOrder } from "./utils/cardOrder";
import { isConvexConfigured } from "./convexClient";
import { useTheme } from "./theme/ThemeProvider";
import AmbientBackground from "./components/AmbientBackground";
import AiCompanion from "./components/AiCompanion";
import { fetchAiSettings } from "./ai/aiApi";
import { PANEL_SCOPE } from "./ai/context";

const STORAGE_KEY = "xDayChallengerData";
// Cloud read budget: a visible tab polls for other-device changes this often,
// and focus/visibility pulls are spaced at least this far apart.
const BACKGROUND_PULL_MS = 10 * 60 * 1000;
const FOCUS_PULL_MIN_GAP_MS = 2 * 60 * 1000;

// Scroll to the goal box and focus it (used by the N shortcut + palette).
const focusGoalInput = () => {
  const el = document.querySelector('input[placeholder="Add a new goal..."]');
  if (!el) return;
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  setTimeout(() => el.focus(), 250);
};
const insertAt = (arr, idx, item) => {
  const a = arr.slice();
  a.splice(Math.min(Math.max(idx, 0), a.length), 0, item);
  return a;
};
// “…” suffix for undo labels; empty when the record has no title/text.
const quoteLabel = (s) => {
  const t = String(s || "").trim();
  return t ? ` “${t.slice(0, 40)}${t.length > 40 ? "…" : ""}”` : "";
};
const DEFAULT_PANEL_ORDER = ["remember", "clipboard", "quickLinks"];
const CONFERENCE_SORT_MODES = new Set([
  "deadline",
  "date",
  "field",
  "added",
  "modified",
  "livedead",
]);

const normalizePanelOrder = (
  order,
  conferencesEnabled,
  publicationsEnabled,
) => {
  const known = [
    "stickyNotes",
    ...DEFAULT_PANEL_ORDER,
    ...(publicationsEnabled ? ["publications"] : []),
    ...(conferencesEnabled ? ["conferences"] : []),
  ];
  const result = (Array.isArray(order) ? order : []).filter((k) =>
    known.includes(k),
  );
  for (const k of known) {
    if (result.includes(k)) continue;
    // The sticky-notes board defaults to the top slot (right under Goals).
    if (k === "stickyNotes") {
      result.unshift(k);
      continue;
    }
    // A newly enabled Publications panel defaults to sitting ABOVE Conferences.
    if (k === "publications") {
      const ci = result.indexOf("conferences");
      if (ci >= 0) {
        result.splice(ci, 0, k);
        continue;
      }
    }
    result.push(k);
  }
  return result;
};

const normalizeConferenceSortMode = (mode) =>
  CONFERENCE_SORT_MODES.has(mode) ? mode : "deadline";
const PUBLICATION_SORT_MODES = new Set([
  "status",
  "submitted",
  "modified",
  "added",
]);
const normalizePublicationSortMode = (mode) =>
  PUBLICATION_SORT_MODES.has(mode) ? mode : "status";
const COOKIE_CONSENT_KEY = "xday_cookie_consent";

// Helper function to create initial tabs structure
const createInitialTabs = (existingGoals = []) => {
  const toDoTab = {
    id: newRecordId(),
    name: "To Do",
    goals: existingGoals.map((goal) => ({
      ...goal,
      deadline: goal.deadline || null, // Preserve deadline if it exists
      important: goal.important || false, // Preserve important if it exists
    })),
  };
  return [toDoTab];
};

function App() {
  // Initial settings
  const [settings, setSettings] = useState({
    challengeName: "My Challenge",
    numberOfDays: 31,
    startDate: formatDate(new Date()),
    userName: "",
    passkey: "",
  });

  const [completedDays, setCompletedDays] = useState(new Set());
  const [partialDays, setPartialDays] = useState(new Set());
  const [tabs, setTabs] = useState(() => createInitialTabs());
  const [activeTabId, setActiveTabId] = useState(null);
  const [subGoals, setSubGoals] = useState([]); // Array of sub-goals with goalId reference
  const [clipboardCards, setClipboardCards] = useState([]);
  const [quickLinkCards, setQuickLinkCards] = useState([]); // "Quick Links" web-link cards
  const [conferenceCards, setConferenceCards] = useState([]); // "Conferences" cards (optional panel)
  const [showConferences, setShowConferences] = useState(false); // Conferences panel toggle (default off)
  const [conferenceSortMode, setConferenceSortMode] = useState("deadline");
  const [publications, setPublications] = useState([]); // "Publications" papers (optional panel)
  const [stickyNotes, setStickyNotes] = useState([]); // Sticky notes (floating on desktop, list on mobile)
  const [notesHidden, setNotesHiddenState] = useState(() => {
    try {
      return localStorage.getItem("xz-notes-hidden") === "1";
    } catch (e) {
      return false;
    }
  });
  const setNotesHidden = (v) => {
    setNotesHiddenState(!!v);
    try {
      localStorage.setItem("xz-notes-hidden", v ? "1" : "0");
    } catch (e) {
      /* ignore */
    }
  };
  const [streakReminderHour, setStreakReminderHour] = useState(19); // Evening streak-at-risk hour (cloud-persisted)
  const [railSide, setRailSide] = useState("left"); // Panel navigator side (cloud-persisted)
  // AI companion (cloud accounts): server-side settings, local awake flag.
  const [aiSettings, setAiSettings] = useState(null);
  const [aiAwake, setAiAwake] = useState(() => {
    try {
      return localStorage.getItem("xz-ai-awake") === "1";
    } catch (e) {
      return false;
    }
  });
  const [aiFocus, setAiFocus] = useState(null); // scope opened from a panel's Ask AI button
  const [aiOpenSignal, setAiOpenSignal] = useState(0);
  const [collapsedPanels, setCollapsedPanels] = useState([]); // Minimized panel keys (cloud-persisted)
  const [notesGathered, setNotesGathered] = useState(false); // Notes gathered onto the board panel (cloud-persisted)
  const [notesSavedLayout, setNotesSavedLayout] = useState(null); // JSON of pre-gather note layout (cloud-persisted)
  const isCollapsed = (key) => collapsedPanels.includes(key);
  const togglePanelCollapsed = (key) =>
    setCollapsedPanels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  const [focusMode, setFocusMode] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [undoItem, setUndoItem] = useState(null); // { key, label, restore, durationMs }
  const [celebration, setCelebration] = useState(null); // { key, message }

  // Undo for deletes: each delete captures the removed record and a restore fn.
  const pushUndo = (label, restore) =>
    setUndoItem({ key: Date.now(), label, restore, durationMs: 6000 });
  const runUndo = () => {
    const it = undoItem;
    setUndoItem(null);
    if (it && it.restore) it.restore();
  };
  const celebrate = (message) => setCelebration({ key: Date.now(), message });
  const [showPublications, setShowPublications] = useState(false); // Publications panel toggle (default off)
  const [publicationSortMode, setPublicationSortMode] = useState("status");
  const [rememberItems, setRememberItems] = useState([]); // Standalone "Remember" checklist
  // When on, incomplete Remember items with a due date also appear in Reminders.
  const [showRememberInReminders, setShowRememberInReminders] = useState(true);
  const [panelOrder, setPanelOrder] = useState(DEFAULT_PANEL_ORDER); // Order of the movable panels
  const [calendarCollapsed, setCalendarCollapsed] = useState(false); // Calendar minimized to "this week" (cloud-persisted)
  const [rememberPrompt, setRememberPrompt] = useState({
    open: false,
    name: "",
    target: null,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [showHelp, setShowHelp] = useState(false); // footer "How to use Xunzhine"
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  // Cookie consent: "granted" | "denied" | null (no choice yet -> show banner).
  const [cookieConsent, setCookieConsent] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem(COOKIE_CONSENT_KEY)
      : null,
  );
  const [legalModal, setLegalModal] = useState({
    open: false,
    type: "privacy",
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [useGoogleSheets, setUseGoogleSheets] = useState(false);
  const [migrationPrompt, setMigrationPrompt] = useState(false);
  const [storageMode, setStorageMode] = useState("");

  // Sync status states
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSynced, setIsSynced] = useState(true);
  // Non-error, dismissible notice (e.g. one-time sync-state upgrade summary) so
  // an upgrade that reconciled pending edits is never silent.
  const [syncNotice, setSyncNotice] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [cloudData, setCloudData] = useState(null);
  const [lastModified, setLastModified] = useState(null);

  // Refs for sync logic
  const wasOfflineRef = useRef(false);
  const pendingSyncRef = useRef(false);
  // Record-level sync refs:
  // - prevStateRef: last full-state snapshot the outbox has accounted for.
  // - suppressDiffRef: set when we apply a load/remote-merge so the edit-time
  //   effect re-baselines instead of treating those changes as local edits.
  // - deviceIdRef: stable device id for LWW tiebreaks.
  // - debouncedFlushRef: debounced cloud flush of the outbox.
  const prevStateRef = useRef(null);
  const suppressDiffRef = useRef(false);
  const deviceIdRef = useRef(getDeviceId());

  // Appearance (theme / skin / ambient) lives in ThemeProvider (localStorage-
  // backed so it applies before login) and is mirrored into cloud settings via
  // buildSnapshot -> settings outbox, so it follows the account across devices.
  // Remote/loaded values are applied through a ref so the [] useCallback merge
  // path below can call it without a stale-closure lint dependency.
  const {
    theme,
    skin,
    ambientEffects,
    skins,
    setTheme,
    setSkin,
    setAmbientEffects,
  } = useTheme();
  const applyAppearanceRef = useRef(() => {});
  applyAppearanceRef.current = (d) => {
    if (!d) return;
    if (typeof d.theme === "string") setTheme(d.theme);
    if (typeof d.skin === "string") setSkin(d.skin);
    if (typeof d.ambientEffects === "boolean")
      setAmbientEffects(d.ambientEffects);
    if (typeof d.calendarCollapsed === "boolean")
      setCalendarCollapsed(d.calendarCollapsed);
    if (typeof d.streakReminderHour === "number")
      setStreakReminderHour(d.streakReminderHour);
    if (d.railSide === "left" || d.railSide === "right")
      setRailSide(d.railSide);
    if (Array.isArray(d.collapsedPanels))
      setCollapsedPanels(
        d.collapsedPanels.filter((k) => typeof k === "string"),
      );
    if (typeof d.notesGathered === "boolean") setNotesGathered(d.notesGathered);
    if (typeof d.notesSavedLayout === "string")
      setNotesSavedLayout(d.notesSavedLayout);
  };
  const debouncedFlushRef = useRef(null);
  const slowFlushRef = useRef(null);
  const lastPullAtRef = useRef(0);

  // Check for existing authentication on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem("xDayAuth");
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        if (authData.username && authData.token) {
          resumeSession(authData.username, authData.token);
        } else if (authData.username && authData.local) {
          enterLocalMode(authData.username);
        } else if (authData.username && authData.passkey) {
          // Legacy saved credentials (pre session-token): convert once.
          handleLogin(authData.username, authData.passkey);
        }
      } catch (error) {
        console.error("Error loading saved auth:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = async () => {
      console.log("Browser went online");
      setIsOnline(true);

      // If we were offline and are using Google Sheets, check for sync.
      // wasOfflineRef is cleared inside checkAndAutoSync ONLY once the cloud is
      // actually reachable — so if this fires before real connectivity returns,
      // the flag stays armed and a later online event will retry.
      if (wasOfflineRef.current && useGoogleSheets && isInitialized) {
        console.log("Was offline, now online - checking for sync...");
        await checkAndAutoSync();
      } else if (!useGoogleSheets || !isInitialized) {
        wasOfflineRef.current = false;
      }
    };

    const handleOffline = () => {
      console.log("Browser went offline");
      setIsOnline(false);
      wasOfflineRef.current = true;
      setError("You are offline. Changes will be saved locally.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Initial check
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useGoogleSheets, isInitialized]);

  // ---------- record-level two-way sync ----------

  // Current full-state snapshot — the shape the sync layer diffs and merges.
  const buildSnapshot = useCallback(
    () => ({
      settings,
      completedDays: Array.from(completedDays),
      partialDays: Array.from(partialDays),
      tabs,
      activeTabId,
      subGoals,
      clipboardCards,
      quickLinkCards,
      conferenceCards,
      showConferences,
      conferenceSortMode,
      publications,
      showPublications,
      publicationSortMode,
      rememberItems,
      showRememberInReminders,
      stickyNotes,
      theme,
      skin,
      ambientEffects,
      calendarCollapsed,
      streakReminderHour,
      railSide,
      collapsedPanels,
      notesGathered,
      notesSavedLayout: notesSavedLayout || undefined,
      panelOrder: normalizePanelOrder(
        panelOrder,
        showConferences,
        showPublications,
      ),
      lastModified: getCurrentTimestamp(),
    }),
    [
      settings,
      completedDays,
      partialDays,
      tabs,
      activeTabId,
      subGoals,
      clipboardCards,
      quickLinkCards,
      conferenceCards,
      showConferences,
      conferenceSortMode,
      publications,
      showPublications,
      publicationSortMode,
      rememberItems,
      showRememberInReminders,
      stickyNotes,
      panelOrder,
      theme,
      skin,
      ambientEffects,
      calendarCollapsed,
      streakReminderHour,
      railSide,
      collapsedPanels,
      notesGathered,
      notesSavedLayout,
    ],
  );

  // Merge a server delta into local state using Last-Writer-Wins, deferring to
  // any newer un-synced local edit recorded in the outbox. Superseded outbox
  // entries (remote won) are dropped so we don't push stale data back.
  const mergeChangesIntoState = useCallback((res) => {
    if (!res) return;
    const outbox = readOutbox();
    const dropKeys = [];
    const wins = (key, incomingUpdatedAt, incomingDeviceId) => {
      const p = outbox[key];
      if (
        p &&
        !isNewer(incomingUpdatedAt, incomingDeviceId, p.updatedAt, p.deviceId)
      ) {
        return false; // local pending edit is newer -> keep local
      }
      if (p) dropKeys.push(key);
      return true;
    };

    // Set ONLY when we actually apply a remote change, so the edit-time effect
    // re-baselines (instead of enqueueing) exactly those merged changes. Setting
    // it unconditionally would leak: a pull with nothing new would leave the flag
    // stuck true, and the user's NEXT real edit would be swallowed (never synced).
    const markRemote = () => {
      suppressDiffRef.current = true;
    };

    if (
      res.settings &&
      wins("settings", res.settings.lastModified, res.settings.deviceId)
    ) {
      markRemote();
      const s = res.settings;
      setSettings((prev) => ({
        ...prev,
        challengeName: s.challengeName,
        numberOfDays: s.numberOfDays,
        startDate: s.startDate,
        userName: prev.userName,
        passkey: prev.passkey,
      }));
      const confOn = !!s.showConferences;
      if (typeof s.showConferences === "boolean") setShowConferences(confOn);
      if (s.conferenceSortMode)
        setConferenceSortMode(
          normalizeConferenceSortMode(s.conferenceSortMode),
        );
      const pubOn = !!s.showPublications;
      if (typeof s.showPublications === "boolean") setShowPublications(pubOn);
      if (typeof s.showRememberInReminders === "boolean")
        setShowRememberInReminders(s.showRememberInReminders);
      if (s.publicationSortMode)
        setPublicationSortMode(
          normalizePublicationSortMode(s.publicationSortMode),
        );
      if (Array.isArray(s.panelOrder))
        setPanelOrder(normalizePanelOrder(s.panelOrder, confOn, pubOn));
      applyAppearanceRef.current(s);
    }

    const dayWins = (res.completedDays || []).filter((R) =>
      wins(`completedDays:${R.date}`, R.updatedAt, R.deviceId),
    );
    if (dayWins.length) {
      markRemote();
      setCompletedDays((prev) => {
        const n = new Set(prev);
        for (const R of dayWins) {
          if (R.deleted || R.status === "partial") n.delete(R.date);
          else n.add(R.date);
        }
        return n;
      });
      setPartialDays((prev) => {
        const n = new Set(prev);
        for (const R of dayWins) {
          if (R.deleted || R.status === "complete") n.delete(R.date);
          else n.add(R.date);
        }
        return n;
      });
    }

    const tabWins = (res.tabs || []).filter((R) =>
      wins(`tabs:${R.id}`, R.updatedAt, R.deviceId),
    );
    const goalWins = (res.goals || []).filter((R) =>
      wins(`goals:${R.id}`, R.updatedAt, R.deviceId),
    );
    if (tabWins.length || goalWins.length) {
      markRemote();
      setTabs((prev) => {
        const map = new Map(
          prev.map((t) => [
            String(t.id),
            { ...t, goals: [...(t.goals || [])] },
          ]),
        );
        for (const T of tabWins) {
          const k = String(T.id);
          if (T.deleted) {
            map.delete(k);
            continue;
          }
          const ex = map.get(k);
          map.set(k, {
            id: T.id,
            name: T.name,
            order: T.order,
            createdAt: T.createdAt,
            goals: ex ? ex.goals : [],
          });
        }
        for (const G of goalWins) {
          const tk = String(G.tabId);
          let tab = map.get(tk);
          if (!tab) {
            if (G.deleted) continue;
            tab = { id: G.tabId, name: "", order: 9999, goals: [] };
            map.set(tk, tab);
          }
          const goals = [...tab.goals];
          const gi = goals.findIndex((x) => String(x.id) === String(G.id));
          if (G.deleted) {
            if (gi >= 0) goals.splice(gi, 1);
          } else {
            const { deleted, ...rec } = G;
            if (gi >= 0) goals[gi] = rec;
            else goals.push(rec);
          }
          map.set(tk, { ...tab, goals });
        }
        return Array.from(map.values()).sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0),
        );
      });
    }

    const mergeList = (setter, table, recs, sortFn) => {
      const winners = (recs || []).filter((R) =>
        wins(`${table}:${R.id}`, R.updatedAt, R.deviceId),
      );
      if (!winners.length) return;
      markRemote();
      setter((prev) => {
        const map = new Map(prev.map((r) => [String(r.id), r]));
        for (const R of winners) {
          const k = String(R.id);
          if (R.deleted) map.delete(k);
          else {
            const { deleted, ...rec } = R;
            map.set(k, rec);
          }
        }
        const arr = Array.from(map.values());
        return sortFn ? sortFn(arr) : arr;
      });
    };
    mergeList(setSubGoals, "subGoals", res.subGoals);
    mergeList(
      setClipboardCards,
      "clipboardCards",
      res.clipboardCards,
      sortByOrder,
    );
    mergeList(
      setQuickLinkCards,
      "quickLinkCards",
      res.quickLinkCards,
      sortByOrder,
    );
    mergeList(
      setRememberItems,
      "rememberItems",
      res.rememberItems,
      sortByOrder,
    );
    mergeList(
      setConferenceCards,
      "conferenceCards",
      res.conferenceCards,
      sortByOrder,
    );
    mergeList(setPublications, "publications", res.publications, (arr) =>
      sortByOrder(arr).map(resolvePublication),
    );
    mergeList(setStickyNotes, "stickyNotes", res.stickyNotes);

    if (dropKeys.length) {
      const fresh = readOutbox();
      for (const k of dropKeys) delete fresh[k];
      writeOutbox(fresh);
    }
  }, []);

  // Pull remote changes and merge them in. Pass { full: true } to force a
  // complete pull (clears the cursor first) — used on open and by "Sync Now" so
  // the device reconciles against the entire cloud state, not just the delta.
  const syncPull = useCallback(
    async ({ full = false } = {}) => {
      if (!useGoogleSheets || !navigator.onLine) return;
      if (full) clearSyncCursor();
      lastPullAtRef.current = Date.now();
      const res = await fetchChangesSince();
      mergeChangesIntoState(res);
      if (res && res.serverTime) setSyncCursor(res.serverTime);
      setLastSyncTime(getCurrentTimestamp());
    },
    [useGoogleSheets, mergeChangesIntoState],
  );

  // Flush the outbox to the cloud and reflect sync status.
  const doFlush = useCallback(async () => {
    if (!useGoogleSheets) return;
    if (!navigator.onLine) {
      const n = outboxCount();
      setHasUnsyncedChanges(n > 0);
      setIsSynced(n === 0);
      return;
    }
    setIsSaving(true);
    try {
      const r = await flushOutbox();
      const remaining =
        r && typeof r.remaining === "number" ? r.remaining : outboxCount();
      setHasUnsyncedChanges(remaining > 0);
      setIsSynced(remaining === 0);
      setLastSyncTime(getCurrentTimestamp());
      setError(null);
      // Never silent: if the server rejected any entries, they were preserved
      // in local quarantine — tell the user in a persistent, dismissible notice.
      if (r && r.quarantined > 0) {
        const c = r.quarantined;
        setSyncNotice(
          `${c} change${c === 1 ? "" : "s"} could not be accepted by the cloud and ${c === 1 ? "was" : "were"} set aside on this device (nothing was deleted). Everything else synced normally.`,
        );
      }
    } catch (e) {
      // Transient network failure: the outbox is intact and auto-retries.
      console.error("Outbox flush failed:", e);
      setHasUnsyncedChanges(true);
      setIsSynced(false);
      setError(
        "Couldn't upload your latest changes — they're saved on this device and will sync automatically once the connection recovers.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [useGoogleSheets]);

  // Debounced flush, kept in a ref so the edit-time effect and the unload
  // handler can trigger it without re-subscribing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFlush = useCallback(debounce(doFlush, 1500), [doFlush]);
  debouncedFlushRef.current = debouncedFlush;
  // Slow lane for preference-only changes (theme, collapsed panels, rail side…):
  // at most one cloud write a minute for those, and they ride along for free
  // whenever a real edit flushes (same outbox).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slowFlush = useCallback(debounce(doFlush, 60000), [doFlush]);
  slowFlushRef.current = slowFlush;

  // Reconnect / periodic auto-sync: pull remote, then push local.
  const checkAndAutoSync = async () => {
    if (!useGoogleSheets || pendingSyncRef.current) return;
    pendingSyncRef.current = true;
    try {
      await syncPull();
      await doFlush();
      setIsOnline(true);
      wasOfflineRef.current = false;
      setError(null);
    } catch (error) {
      console.error("Error during auto-sync:", error);
      setIsOnline(navigator.onLine);
    } finally {
      pendingSyncRef.current = false;
    }
  };

  // Load cloud data into state
  const loadCloudDataIntoState = (data) => {
    if (!data) return;

    setSettings((prev) => ({
      ...(data.settings || {
        challengeName: "My Challenge",
        numberOfDays: 31,
        startDate: formatDate(new Date()),
      }),
      userName: prev.userName,
      passkey: prev.passkey,
    }));
    setCompletedDays(new Set(data.completedDays || []));
    setPartialDays(new Set(data.partialDays || []));
    const conferencesOn = Boolean(data.showConferences);
    const publicationsOn = Boolean(data.showPublications);
    setClipboardCards(sortByOrder(data.clipboardCards || []));
    setQuickLinkCards(sortByOrder(data.quickLinkCards || []));
    setConferenceCards(sortByOrder(data.conferenceCards || []));
    setShowConferences(conferencesOn);
    setConferenceSortMode(normalizeConferenceSortMode(data.conferenceSortMode));
    setPublications(
      sortByOrder(data.publications || []).map(resolvePublication),
    );
    setShowPublications(publicationsOn);
    setPublicationSortMode(
      normalizePublicationSortMode(data.publicationSortMode),
    );
    setShowRememberInReminders(
      typeof data.showRememberInReminders === "boolean"
        ? data.showRememberInReminders
        : true,
    );
    setSubGoals(data.subGoals || []);
    setRememberItems(sortByOrder(data.rememberItems || []));
    setPanelOrder(
      normalizePanelOrder(data.panelOrder, conferencesOn, publicationsOn),
    );
    setLastModified(data.lastModified || null);
    applyAppearanceRef.current(data);
    setStickyNotes(Array.isArray(data.stickyNotes) ? data.stickyNotes : []);

    if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
      setTabs(data.tabs);
      setActiveTabId(data.activeTabId || data.tabs[0].id);
    } else {
      const initialTabs = createInitialTabs();
      setTabs(initialTabs);
      setActiveTabId(initialTabs[0].id);
    }
  };

  // Manual sync ("Sync Now"): push local changes, then do a FULL pull so the
  // user always gets the complete latest cloud state. Record-level merge means
  // there is nothing to "resolve" — LWW reconciles both sides.
  const handleManualSync = async () => {
    if (!useGoogleSheets || !isOnline) return;
    setIsSaving(true);
    setError(null);
    try {
      await doFlush();
      // doFlush manages the spinner itself and turns it off when done; keep it
      // on through the pull phase so the indicator doesn't flicker mid-sync.
      setIsSaving(true);
      await syncPull({ full: true });
      const n = outboxCount();
      setIsSynced(n === 0);
      setHasUnsyncedChanges(n > 0);
      setLastSyncTime(getCurrentTimestamp());
      if (n > 0) {
        // The flush left entries behind (its own catch/notice explains why) —
        // make sure the user knows the sync wasn't fully clean.
        setError(
          (prev) =>
            prev ||
            "Some changes haven't reached the cloud yet — they'll retry automatically.",
        );
      }
    } catch (error) {
      console.error("Error during manual sync:", error);
      setError("Sync failed. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle conflict resolution
  const handleConflictResolution = async (selections, itemSelections) => {
    try {
      setIsSaving(true);

      // Get current local data
      const localData = {
        settings,
        completedDays: Array.from(completedDays),
        partialDays: Array.from(partialDays),
        tabs,
        activeTabId,
        subGoals,
        clipboardCards,
        quickLinkCards,
        conferenceCards,
        showConferences,
        conferenceSortMode,
        publications,
        showPublications,
        publicationSortMode,
        rememberItems,
        showRememberInReminders,
        panelOrder: normalizePanelOrder(
          panelOrder,
          showConferences,
          showPublications,
        ),
        lastModified,
      };

      // Merge data based on selections
      const mergedData = mergeDataWithSelections(
        localData,
        cloudData,
        selections,
        itemSelections,
      );

      // Save merged data to both cloud and local
      await saveAllData(mergedData);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedData));

      // Load merged data into state
      loadCloudDataIntoState(mergedData);
      setLastModified(mergedData.lastModified);
      setLastSyncTime(getCurrentTimestamp());
      setIsSynced(true);
      setHasUnsyncedChanges(false);
      setShowConflictModal(false);
      setConflictData(null);
      setError(null);
    } catch (error) {
      console.error("Error resolving conflict:", error);
      setError("Failed to resolve conflict. Please try again.");
    }

    setIsSaving(false);
  };

  // After a server-side snapshot restore, reload the (now restored) cloud data
  // into state and refresh the local mirror so the UI reflects the rollback.
  const handleAfterRestore = async () => {
    try {
      const data = await fetchAllData(); // authoritative full state after restore
      loadCloudDataIntoState(data);
      // The restore is authoritative: re-baseline, drop any pending local edits,
      // and force the next pull to be full so the change feed re-syncs cleanly.
      suppressDiffRef.current = true;
      writeOutbox({});
      clearSyncCursor();
      setCloudData(data);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setLastModified(data.lastModified || null);
      applyAppearanceRef.current(data);
      setStickyNotes(Array.isArray(data.stickyNotes) ? data.stickyNotes : []);
      setLastSyncTime(getCurrentTimestamp());
      setIsSynced(true);
      setHasUnsyncedChanges(false);
      setError(null);
    } catch (e) {
      console.error("Failed to reload after restore:", e);
      setError("Restored, but failed to reload. Please refresh the page.");
    }
  };

  // ---- Auth ----
  // The passkey is verified by Convex (auth:login) which mints a session token;
  // only { username, token } is kept on this device. Users without a cloud
  // account can still use the app in local-only mode.
  const enterCloudMode = async (username) => {
    setUseGoogleSheets(true);
    trackLogin("cloud");
    setSettings((prev) => ({ ...prev, userName: username, passkey: "" }));
    setStorageMode("Cloud");
    setShowLogin(false);
    setLoginError(null);
    await loadData(true);
  };

  const enterLocalMode = async (username) => {
    localStorage.setItem("xDayAuth", JSON.stringify({ username, local: true }));
    setUseGoogleSheets(false);
    trackLogin("local");
    setSettings((prev) => ({ ...prev, userName: username, passkey: "" }));
    setStorageMode("Local");
    setShowLogin(false);
    setLoginError(null);
    await loadData(false);
  };

  const handleLogin = async (username, passkey) => {
    if (!isConvexConfigured()) {
      await enterLocalMode(username);
      return;
    }
    setLoginBusy(true);
    setLoginError(null);
    try {
      const session = await cloudLogin(username, passkey);
      localStorage.setItem(
        "xDayAuth",
        JSON.stringify({ username: session.userName, token: session.token }),
      );
      await enterCloudMode(session.userName);
    } catch (e) {
      const code = e && e.data && e.data.code;
      if (code === "bad_credentials") {
        setLoginError("Wrong username or passkey.");
      } else if (code === "not_configured") {
        setLoginError("Cloud login isn't configured on the server yet.");
      } else {
        setLoginError(
          "Couldn't reach the cloud. Check your connection and try again.",
        );
      }
    } finally {
      setLoginBusy(false);
    }
  };

  // "Forgot passkey?": a one-time recovery code sets a new passkey server-side
  // (revoking every other session) and signs this device in.
  const handleRecover = async ({ userName, code, next }) => {
    if (!isConvexConfigured()) return;
    setLoginBusy(true);
    setLoginError(null);
    try {
      const session = await resetPasskeyWithRecovery({ userName, code, next });
      localStorage.setItem(
        "xDayAuth",
        JSON.stringify({ username: session.userName, token: session.token }),
      );
      await enterCloudMode(session.userName);
    } catch (e) {
      const code2 = e && e.data && e.data.code;
      if (code2 === "bad_recovery_code") {
        setLoginError("That recovery code is not valid (or was already used).");
      } else if (code2 === "no_recovery") {
        setLoginError(
          "No recovery codes exist for this account yet — reset the passkey from the Convex dashboard.",
        );
      } else if (code2 === "weak_passkey") {
        setLoginError("The new passkey must be at least 6 characters.");
      } else {
        setLoginError(
          "Couldn't reach the cloud. Check your connection and try again.",
        );
      }
    } finally {
      setLoginBusy(false);
    }
  };

  // Saved token: verify it when we can, but never block an offline start —
  // loadData shows local data first and syncs when the network is back.
  const resumeSession = async (username, token) => {
    let valid = true;
    try {
      const me = await cloudMe(token);
      if (me === null) valid = false;
    } catch (e) {
      valid = true; // network problem — assume valid, sync will retry
    }
    if (!valid) {
      localStorage.removeItem("xDayAuth");
      setLoginError("Your session expired — please sign in again.");
      setShowLogin(true);
      return;
    }
    await enterCloudMode(username);
  };

  // Load data. Always loads LOCAL first (instant, offline-safe, preserves any
  // pending offline edits), then — for cloud users — pulls the remote delta and
  // merges it in (LWW), and flushes the outbox. There is no full-replace and no
  // timestamp-conflict modal: record-level merge reconciles both sides.
  const loadData = async (shouldUseCloud) => {
    setIsLoading(true);
    setError(null);

    let localData = null;
    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      localData = savedData ? JSON.parse(savedData) : null;
    } catch (e) {
      localData = null;
    }
    if (localData) {
      loadLocalData(localData);
    } else if (shouldUseCloud && isConvexConfigured()) {
      // Fresh device, no local data: start empty so the pull populates without
      // creating a duplicate default tab alongside the cloud's.
      setTabs([]);
      setActiveTabId(null);
    } else {
      const initialTabs = createInitialTabs();
      setTabs(initialTabs);
      setActiveTabId(initialTabs[0].id);
    }
    // The load above is not a user edit — re-baseline on the next effect run.
    suppressDiffRef.current = true;

    if (shouldUseCloud && isConvexConfigured()) {
      try {
        // Upgrade safety: shed any poisoned sync state left by older builds
        // (stale outbox / cursor / bootstrap flag). It's archived, not destroyed
        // — if it held pending edits we surface a visible notice further down so
        // nothing is ever silently dropped.
        const migration = migrateSyncStateIfNeeded();

        // ALWAYS full-pull on open so every device reconciles to the complete
        // cloud truth. Clearing the cursor forces getChangesSince to return
        // everything; the merge applies it by LWW and defers only to genuinely
        // newer un-synced local edits already in the outbox.
        clearSyncCursor();
        const res = await fetchChangesSince(); // full pull (no cursor)
        mergeChangesIntoState(res);
        if (res && res.serverTime) setSyncCursor(res.serverTime);
        setLastSyncTime(getCurrentTimestamp());

        const cloudEmpty =
          !res || (!res.settings && (!res.tabs || res.tabs.length === 0));
        if (!localData && cloudEmpty) {
          // Brand-new account (nothing local or remote): seed the default tab
          // and enqueue it so the account starts with a "To Do" tab.
          const initialTabs = createInitialTabs();
          setTabs(initialTabs);
          setActiveTabId(initialTabs[0].id);
          enqueueChanges(
            buildBootstrapEntries(
              {
                settings: {
                  challengeName: "My Challenge",
                  numberOfDays: 31,
                  startDate: formatDate(new Date()),
                },
                completedDays: [],
                partialDays: [],
                tabs: initialTabs,
                subGoals: [],
                clipboardCards: [],
                quickLinkCards: [],
                conferenceCards: [],
                publications: [],
                rememberItems: [],
                panelOrder: DEFAULT_PANEL_ORDER,
                showConferences: false,
                showPublications: false,
                showRememberInReminders: true,
              },
              deviceIdRef.current,
            ),
          );
        } else if (localData) {
          // Reconcile local-only data UP to the cloud: enqueue ONLY records the
          // cloud has never seen (offline/pre-sync data), with safe stamps. A
          // stale local copy of a record the cloud already has is never pushed,
          // so it can never clobber the cloud — the full pull above is the single
          // source of truth for shared records.
          enqueueChanges(
            buildLocalOnlyEntries(localData, res, deviceIdRef.current),
          );
        }

        await doFlush(); // push local-only/seed + any genuine pending offline edits
        setStorageMode("Cloud");
        setIsOnline(true);
        wasOfflineRef.current = false;
        const n = outboxCount();
        setIsSynced(n === 0);
        setHasUnsyncedChanges(n > 0);
        setLastSyncTime(getCurrentTimestamp());
        setError(null);

        // Never silently drop pending edits across a sync upgrade.
        if (migration.migrated && migration.archivedCount > 0) {
          const c = migration.archivedCount;
          setSyncNotice(
            `Sync was upgraded on this device. ${c} pending change${c === 1 ? "" : "s"} from the previous version ${c === 1 ? "was" : "were"} reconciled with the cloud, and a backup was saved. Please double-check your latest edits.`,
          );
        }
      } catch (e) {
        // Cloud unreachable (opened offline / transient). Stay in cloud mode,
        // keep local data, arm reconnect so we sync when the network returns.
        console.error("Cloud sync on load failed; staying local for now:", e);
        setIsOnline(navigator.onLine);
        wasOfflineRef.current = true;
        setStorageMode("Cloud (reconnecting…)");
        setError(
          "Couldn't reach the cloud right now. Your changes are saved locally and will sync automatically when the connection is back.",
        );
        const n = outboxCount();
        setHasUnsyncedChanges(n > 0);
        setIsSynced(n === 0);
      }
    }

    setIsLoading(false);
    setIsInitialized(true);
  };

  // Helper function to load data from localStorage
  const loadLocalData = (data) => {
    setSettings((prev) => ({
      ...(data.settings || {
        challengeName: "My Challenge",
        numberOfDays: 31,
        startDate: formatDate(new Date()),
      }),
      userName: prev.userName,
      passkey: prev.passkey,
    }));
    setCompletedDays(new Set(data.completedDays || []));
    setPartialDays(new Set(data.partialDays || []));
    const conferencesOn = Boolean(data.showConferences);
    const publicationsOn = Boolean(data.showPublications);
    setClipboardCards(sortByOrder(data.clipboardCards || []));
    setQuickLinkCards(sortByOrder(data.quickLinkCards || []));
    setConferenceCards(sortByOrder(data.conferenceCards || []));
    setShowConferences(conferencesOn);
    setConferenceSortMode(normalizeConferenceSortMode(data.conferenceSortMode));
    setPublications(
      sortByOrder(data.publications || []).map(resolvePublication),
    );
    setShowPublications(publicationsOn);
    setPublicationSortMode(
      normalizePublicationSortMode(data.publicationSortMode),
    );
    setShowRememberInReminders(
      typeof data.showRememberInReminders === "boolean"
        ? data.showRememberInReminders
        : true,
    );
    setSubGoals(data.subGoals || []);
    setRememberItems(sortByOrder(data.rememberItems || []));
    setPanelOrder(
      normalizePanelOrder(data.panelOrder, conferencesOn, publicationsOn),
    );
    setLastModified(data.lastModified || null);
    applyAppearanceRef.current(data);
    setStickyNotes(Array.isArray(data.stickyNotes) ? data.stickyNotes : []);

    if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
      setTabs(data.tabs);
      setActiveTabId(data.activeTabId || data.tabs[0].id);
    } else if (data.goals && Array.isArray(data.goals)) {
      const migratedTabs = createInitialTabs(data.goals);
      setTabs(migratedTabs);
      setActiveTabId(migratedTabs[0].id);
    } else {
      const initialTabs = createInitialTabs();
      setTabs(initialTabs);
      setActiveTabId(initialTabs[0].id);
    }
  };

  // ---------- autosave: edit-time outbox + debounced cloud flush ----------
  // Runs immediately on every state change. Always writes a full local backup.
  // For cloud users it diffs the new snapshot against the last-accounted one and
  // records the changed/removed records into the persisted outbox stamped at
  // EDIT time, then schedules a debounced flush. Load / remote-merge changes set
  // suppressDiffRef so they re-baseline instead of being treated as local edits.
  useEffect(() => {
    if (!isInitialized) return;
    const curr = buildSnapshot();

    // Full local backup (offline-safe, survives reload) for every user.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(curr));
    } catch (e) {
      /* ignore quota errors */
    }

    if (suppressDiffRef.current) {
      prevStateRef.current = curr;
      suppressDiffRef.current = false;
      return;
    }

    if (useGoogleSheets && prevStateRef.current) {
      const entries = diffStates(
        prevStateRef.current,
        curr,
        deviceIdRef.current,
        getCurrentTimestamp(),
      );
      if (entries.length) {
        enqueueChanges(entries);
        setLastModified(curr.lastModified);
        setHasUnsyncedChanges(true);
        setIsSynced(false);
        if (isUiOnlyChange(prevStateRef.current, curr, entries)) {
          slowFlushRef.current?.();
        } else {
          debouncedFlushRef.current?.();
        }
      }
    }
    prevStateRef.current = curr;
    // buildSnapshot is memoized over every piece of synced state, so its
    // identity changes on any edit — that's what drives this effect.
  }, [isInitialized, useGoogleSheets, buildSnapshot]);

  // Slow background pull so another device's changes arrive while the tab is
  // open AND visible. Hidden tabs never poll; coming back to the tab pulls
  // (below). Every pull is a cloud read, so this stays deliberately slow.
  useEffect(() => {
    if (!isInitialized || !useGoogleSheets) return;
    const id = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        navigator.onLine &&
        !pendingSyncRef.current
      ) {
        checkAndAutoSync();
      }
    }, BACKGROUND_PULL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, useGoogleSheets]);

  // Pull when the app regains focus / becomes visible so switching back to an
  // already-open tab reflects edits made on another device — but not more than
  // once every couple of minutes: window focus/blur fires constantly while
  // working next to the app, and each pull is a cloud read.
  useEffect(() => {
    if (!isInitialized || !useGoogleSheets) return;
    const syncIfActive = () => {
      if (
        document.visibilityState === "visible" &&
        navigator.onLine &&
        !pendingSyncRef.current &&
        Date.now() - lastPullAtRef.current > FOCUS_PULL_MIN_GAP_MS
      ) {
        checkAndAutoSync();
      }
    };
    window.addEventListener("focus", syncIfActive);
    document.addEventListener("visibilitychange", syncIfActive);
    return () => {
      window.removeEventListener("focus", syncIfActive);
      document.removeEventListener("visibilitychange", syncIfActive);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, useGoogleSheets]);

  // Flush pending changes when the tab closes (best-effort; the outbox is
  // persisted so anything not sent flushes on the next load).
  useEffect(() => {
    const handleUnload = () => {
      debouncedFlushRef.current?.flush?.();
      slowFlushRef.current?.flush?.();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  // AI companion settings (name, provider, model, scopes…) live in the cloud.
  useEffect(() => {
    if (!isInitialized || !useGoogleSheets) {
      setAiSettings(null);
      return;
    }
    let cancelled = false;
    fetchAiSettings()
      .then((s) => !cancelled && setAiSettings(s))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isInitialized, useGoogleSheets]);

  // Keep activeTabId valid (e.g. after an empty-start cloud load populates tabs).
  useEffect(() => {
    if (isInitialized && activeTabId == null && tabs.length > 0) {
      setActiveTabId(tabs[0].id);
    }
  }, [isInitialized, activeTabId, tabs]);

  // Generate challenge dates based on settings
  const challengeDates = generateChallengeDates(
    settings.startDate,
    settings.numberOfDays,
  );

  // Organize dates into weeks (Sunday to Saturday)
  const organizeIntoWeeks = (dates) => {
    if (dates.length === 0) return [];

    const weeks = [];
    let currentWeek = [];

    // Get the day of week for the first date (0 = Sunday, 6 = Saturday)
    const firstDate = new Date(dates[0].date);
    const firstDayOfWeek = firstDate.getDay(); // 0 = Sunday, 6 = Saturday

    // Add empty cells for days before the start date
    for (let i = 0; i < firstDayOfWeek; i++) {
      currentWeek.push(null);
    }

    // Add all dates
    dates.forEach((day) => {
      currentWeek.push(day);

      // If we've filled a week (7 days), start a new week
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    // Add empty cells for the last incomplete week if needed
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const weeks = organizeIntoWeeks(challengeDates);

  // Calculate statistics
  const completedCount = completedDays.size;
  const totalDays = settings.numberOfDays;
  const currentStreak = calculateStreak(completedDays, challengeDates);
  const bestStreak = calculateBestStreak(completedDays, challengeDates);

  // Calculate missed days (past days that are neither complete nor partial)
  const missedDays = challengeDates.filter(
    (day) =>
      day.isPast && !completedDays.has(day.date) && !partialDays.has(day.date),
  ).length;

  // Today's entry + status for the sidebar "Mark today" control.
  // "none" | "done" | "partial" | null (today is outside the challenge window).
  const todayEntry = challengeDates.find((d) => d.isToday) || null;
  const todayStatus = todayEntry
    ? completedDays.has(todayEntry.date)
      ? "done"
      : partialDays.has(todayEntry.date)
        ? "partial"
        : "none"
    : null;

  // Cycle through day status: incomplete -> complete -> partial -> incomplete
  const toggleDay = (date) => {
    const newCompleted = new Set(completedDays);
    const newPartial = new Set(partialDays);

    if (newCompleted.has(date)) {
      // complete -> partial
      newCompleted.delete(date);
      newPartial.add(date);
    } else if (newPartial.has(date)) {
      // partial -> incomplete
      newPartial.delete(date);
    } else {
      // incomplete -> complete
      newCompleted.add(date);
    }

    setCompletedDays(newCompleted);
    setPartialDays(newPartial);

    // Celebrate completing today (and streak milestones).
    const completedNow = newCompleted.has(date) && !completedDays.has(date);
    const isTodayDate = challengeDates.some(
      (d) => d.isToday && d.date === date,
    );
    if (completedNow && isTodayDate) {
      const streak = calculateStreak(newCompleted, challengeDates);
      const milestones = [3, 7, 14, 21, 30, 50, 66, 100, 150, 200, 365];
      celebrate(
        milestones.includes(streak)
          ? `🔥 ${streak}-day streak!`
          : streak > 1
            ? `Day done · ${streak}-day streak`
            : "Day done!",
      );
    }
  };

  // Tab management
  const addTab = () => {
    const newTab = {
      id: newRecordId(),
      name: `Tab ${tabs.length + 1}`,
      goals: [],
    };
    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
  };

  const deleteTab = (tabId) => {
    const tabToDelete = tabs.find((t) => t.id === tabId);
    if (tabToDelete && tabToDelete.name === "To Do") {
      alert('Cannot delete the "To Do" tab');
      return;
    }
    const newTabs = tabs.filter((t) => t.id !== tabId);
    if (newTabs.length > 0) {
      const idx = tabs.findIndex((t) => t.id === tabId);
      const wasActive = activeTabId === tabId;
      setTabs(newTabs);
      if (wasActive) {
        setActiveTabId(newTabs[0].id);
      }
      if (tabToDelete)
        pushUndo(`Deleted tab “${tabToDelete.name}”`, () => {
          setTabs((prev) =>
            prev.some((t) => t.id === tabToDelete.id)
              ? prev
              : insertAt(prev, idx, tabToDelete),
          );
          if (wasActive) setActiveTabId(tabToDelete.id);
        });
    }
  };

  const renameTab = (tabId, newName) => {
    const tabToRename = tabs.find((t) => t.id === tabId);
    if (tabToRename && tabToRename.name === "To Do" && newName !== "To Do") {
      alert('Cannot rename the "To Do" tab');
      return;
    }
    setTabs(
      tabs.map((tab) => (tab.id === tabId ? { ...tab, name: newName } : tab)),
    );
  };

  // Reorder tabs (drag a tab onto another tab to insert before it).
  const reorderTabs = (draggedTabId, targetTabId) => {
    if (draggedTabId === targetTabId) return;
    setTabs((prev) => {
      const fromIndex = prev.findIndex((t) => t.id === draggedTabId);
      const targetIndex = prev.findIndex((t) => t.id === targetTabId);
      if (fromIndex === -1 || targetIndex === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      const insertIndex = next.findIndex((t) => t.id === targetTabId);
      next.splice(insertIndex, 0, moved);
      return next;
    });
  };

  // Goal management
  const addGoal = (tabId, text, deadline = null) => {
    const newGoal = {
      id: newRecordId(),
      text,
      completed: false,
      deadline: deadline || null,
      important: false,
    };
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId ? { ...tab, goals: [...tab.goals, newGoal] } : tab,
      ),
    );
  };

  const toggleGoal = (tabId, goalId) => {
    // Track only the transition into "completed".
    const tabForGoal = tabs.find((t) => t.id === tabId);
    const goalBefore = tabForGoal?.goals.find((g) => g.id === goalId);
    if (goalBefore && !goalBefore.completed) {
      trackGoalCompleted();
    }
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              goals: tab.goals.map((goal) =>
                goal.id === goalId
                  ? {
                      ...goal,
                      completed: !goal.completed,
                      // Record when it was marked complete; clear on un-complete.
                      completedAt: !goal.completed
                        ? new Date().toISOString()
                        : null,
                    }
                  : goal,
              ),
            }
          : tab,
      ),
    );
  };

  const deleteGoal = (tabId, goalId) => {
    const tab = tabs.find((t) => t.id === tabId);
    const idx = tab ? tab.goals.findIndex((g) => g.id === goalId) : -1;
    const goal = idx >= 0 ? tab.goals[idx] : null;
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId
          ? { ...tab, goals: tab.goals.filter((goal) => goal.id !== goalId) }
          : tab,
      ),
    );
    if (goal)
      pushUndo(`Deleted goal “${String(goal.text).slice(0, 40)}”`, () =>
        setTabs((prev) =>
          prev.map((t) =>
            t.id === tabId && !t.goals.some((g) => g.id === goal.id)
              ? { ...t, goals: insertAt(t.goals, idx, goal) }
              : t,
          ),
        ),
      );
  };

  const editGoal = (tabId, goalId, newText) => {
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              goals: tab.goals.map((goal) =>
                goal.id === goalId ? { ...goal, text: newText } : goal,
              ),
            }
          : tab,
      ),
    );
  };

  const updateGoalDeadline = (tabId, goalId, deadline) => {
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              goals: tab.goals.map((goal) =>
                goal.id === goalId ? { ...goal, deadline } : goal,
              ),
            }
          : tab,
      ),
    );
  };

  const toggleGoalImportance = (tabId, goalId) => {
    setTabs(
      tabs.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              goals: tab.goals.map((goal) =>
                goal.id === goalId
                  ? { ...goal, important: !goal.important }
                  : goal,
              ),
            }
          : tab,
      ),
    );
  };

  const moveGoalUp = (tabId, index) => {
    if (index > 0) {
      setTabs(
        tabs.map((tab) => {
          if (tab.id === tabId) {
            const newGoals = [...tab.goals];
            [newGoals[index - 1], newGoals[index]] = [
              newGoals[index],
              newGoals[index - 1],
            ];
            return { ...tab, goals: newGoals };
          }
          return tab;
        }),
      );
    }
  };

  const moveGoalDown = (tabId, index) => {
    setTabs(
      tabs.map((tab) => {
        if (tab.id === tabId && index < tab.goals.length - 1) {
          const newGoals = [...tab.goals];
          [newGoals[index], newGoals[index + 1]] = [
            newGoals[index + 1],
            newGoals[index],
          ];
          return { ...tab, goals: newGoals };
        }
        return tab;
      }),
    );
  };

  // Drag and drop handlers
  const moveGoalWithinTab = (tabId, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setTabs(
      tabs.map((tab) => {
        if (tab.id === tabId) {
          const newGoals = [...tab.goals];
          const [removed] = newGoals.splice(fromIndex, 1);
          newGoals.splice(toIndex, 0, removed);
          return { ...tab, goals: newGoals };
        }
        return tab;
      }),
    );
  };

  const moveGoalBetweenTabs = (fromTabId, toTabId, goalId, toIndex) => {
    setTabs(
      tabs.map((tab) => {
        if (tab.id === fromTabId) {
          // Remove goal from source tab
          return { ...tab, goals: tab.goals.filter((g) => g.id !== goalId) };
        } else if (tab.id === toTabId) {
          // Add goal to target tab
          const goal = tabs
            .find((t) => t.id === fromTabId)
            ?.goals.find((g) => g.id === goalId);
          if (goal) {
            const newGoals = [...tab.goals];
            newGoals.splice(toIndex, 0, goal);
            return { ...tab, goals: newGoals };
          }
        }
        return tab;
      }),
    );
  };

  // Clipboard management
  const addClipboardCard = (card) => {
    setClipboardCards((prev) => [...prev, { ...card, order: prev.length }]);
  };

  const updateClipboardCard = (cardId, updates) => {
    setClipboardCards(
      clipboardCards.map((card) =>
        card.id === cardId ? { ...card, ...updates } : card,
      ),
    );
  };

  const deleteClipboardCard = (cardId) => {
    const idx = clipboardCards.findIndex((x) => x.id === cardId);
    const item = idx >= 0 ? clipboardCards[idx] : null;
    setClipboardCards((prev) => prev.filter((x) => x.id !== cardId));
    if (item)
      pushUndo(
        `Deleted clipboard card${quoteLabel(item.title || item.text)}`,
        () =>
          setClipboardCards((prev) =>
            prev.some((x) => x.id === item.id)
              ? prev
              : insertAt(prev, idx, item),
          ),
      );
  };

  // Wiping every clipboard card needs three separate confirmations; the last
  // one must be typed so it can't be clicked through by reflex.
  const deleteAllClipboardCards = () => {
    const count = clipboardCards.length;
    if (count === 0) return;
    const label = `${count} clipboard card${count === 1 ? "" : "s"}`;
    if (!window.confirm(`Delete all ${label}? (confirmation 1 of 3)`)) return;
    if (
      !window.confirm(
        `Are you sure? Every clipboard card will be removed on all your devices. (confirmation 2 of 3)`,
      )
    )
      return;
    const typed = window.prompt(
      `Final confirmation (3 of 3): type DELETE to permanently remove all ${label}.`,
    );
    if ((typed || "").trim() !== "DELETE") return;
    const removed = clipboardCards;
    setClipboardCards([]);
    pushUndo(`Deleted all ${label}`, () =>
      setClipboardCards((prev) => {
        const ids = new Set(prev.map((x) => String(x.id)));
        return [...removed.filter((x) => !ids.has(String(x.id))), ...prev];
      }),
    );
  };

  const movePanel = (panelKey, direction) => {
    trackPanelReorder(panelKey, direction);
    setPanelOrder((prev) => {
      const order = normalizePanelOrder(
        prev,
        showConferences,
        showPublications,
      );
      const index = order.indexOf(panelKey);
      if (index === -1) return order;
      // The board is desktop-only, so on phones step over it.
      const boardHidden =
        typeof window !== "undefined" &&
        window.matchMedia &&
        !window.matchMedia("(min-width: 768px)").matches;
      const isHidden = (k) => k === "stickyNotes" && boardHidden;
      let swapWith = direction === "up" ? index - 1 : index + 1;
      while (
        swapWith >= 0 &&
        swapWith < order.length &&
        isHidden(order[swapWith])
      )
        swapWith += direction === "up" ? -1 : 1;
      if (swapWith < 0 || swapWith >= order.length) return order;
      const next = [...order];
      [next[index], next[swapWith]] = [next[swapWith], next[index]];
      return next;
    });
  };

  // ---- Cookie consent + analytics bootstrap ----
  // Initialize analytics once if consent was already granted in a prior visit.
  useEffect(() => {
    if (cookieConsent === "granted") {
      initAnalytics();
    }
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "granted");
    setCookieConsent("granted");
    initAnalytics();
  };

  const declineCookies = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "denied");
    setCookieConsent("denied");
  };

  const openCookieSettings = () => {
    // Re-show the banner so the user can change their choice.
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setCookieConsent(null);
  };

  const openLegal = (type) => setLegalModal({ open: true, type });
  const closeLegal = () => setLegalModal((prev) => ({ ...prev, open: false }));

  const reorderClipboardCards = (draggedCardId, targetCardId) => {
    if (draggedCardId === targetCardId) return;
    setClipboardCards((prev) => {
      const fromIndex = prev.findIndex((c) => c.id === draggedCardId);
      const targetIndex = prev.findIndex((c) => c.id === targetCardId);
      if (fromIndex === -1 || targetIndex === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      const insertIndex = next.findIndex((c) => c.id === targetCardId);
      next.splice(insertIndex, 0, moved);
      return withSequentialOrder(next);
    });
  };

  // Quick Links management
  // Card completion is derived from its links: a card is complete when it has
  // links and all of them are checked. This keeps a single source of truth so
  // ticking in the card, Remember or Reminders panels all stay consistent.
  const deriveCardCompleted = (links) =>
    Array.isArray(links) && links.length > 0 && links.every((l) => l.completed);

  const addQuickLinkCard = (card) => {
    setQuickLinkCards((prev) => [...prev, { ...card, order: prev.length }]);
  };

  const updateQuickLinkCard = (cardId, updates) => {
    setQuickLinkCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        const merged = { ...card, ...updates };
        return {
          ...merged,
          completed: deriveCardCompleted(merged.links),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const deleteQuickLinkCard = (cardId) => {
    const idx = quickLinkCards.findIndex((x) => x.id === cardId);
    const item = idx >= 0 ? quickLinkCards[idx] : null;
    setQuickLinkCards((prev) => prev.filter((x) => x.id !== cardId));
    if (item)
      pushUndo(
        `Deleted quick link card${quoteLabel(item.title || item.text)}`,
        () =>
          setQuickLinkCards((prev) =>
            prev.some((x) => x.id === item.id)
              ? prev
              : insertAt(prev, idx, item),
          ),
      );
  };

  const deleteAllQuickLinkCards = () => {
    setQuickLinkCards([]);
  };

  // Toggle one link's completed state; the card auto-completes when all done.
  const toggleQuickLinkLink = (cardId, linkId) => {
    setQuickLinkCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        const links = (card.links || []).map((l) =>
          l.id === linkId ? { ...l, completed: !l.completed } : l,
        );
        return {
          ...card,
          links,
          completed: deriveCardCompleted(links),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  // Card-level toggle (from the card, Remember or Reminders panels): check or
  // uncheck every link at once so the derived completion stays consistent.
  const toggleQuickLinkCard = (cardId) => {
    setQuickLinkCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        const nextCompleted = !card.completed;
        const links = (card.links || []).map((l) => ({
          ...l,
          completed: nextCompleted,
        }));
        return {
          ...card,
          links,
          completed: nextCompleted,
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const updateQuickLinkCardDueDate = (cardId, dueDate) => {
    setQuickLinkCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? {
              ...card,
              dueDate: dueDate || null,
              updatedAt: new Date().toISOString(),
            }
          : card,
      ),
    );
  };

  // Derived view models for the Remember / Reminders panels.
  // Remember: any card with the remember tag on (date optional).
  const quickLinkRememberItems = quickLinkCards
    .filter((card) => card.remember)
    .map((card) => ({
      id: card.id,
      text: card.title,
      completed: card.completed,
      dueDate: card.dueDate || null,
      source: "quicklink",
    }));

  // Reminders: cards with remember on AND a due date that are not yet complete.
  const quickLinkReminderItems = quickLinkCards
    .filter((card) => card.remember && card.dueDate && !card.completed)
    .map((card) => ({
      id: card.id,
      text: card.title,
      completed: card.completed,
      deadline: card.dueDate,
      tabName: "Quick Link",
      isQuickLink: true,
    }));

  // Reminders: the single next upcoming occurrence of each active recurring
  // Remember item.
  const recurringReminderItems = rememberItems
    .filter((item) => isRecurring(item) && !item.completed)
    .map((item) => {
      const next = getNextOccurrence(item);
      if (!next) return null;
      return {
        id: item.id,
        text: item.text,
        completed: false,
        deadline: next,
        tabName: "Recurring",
        isRecurring: true,
      };
    })
    .filter(Boolean);

  // Reminders: one-off Remember items with a due date, gated by the panel toggle.
  const rememberReminderItems = showRememberInReminders
    ? rememberItems
        .filter((item) => !isRecurring(item) && item.dueDate && !item.completed)
        .map((item) => ({
          id: item.id,
          text: item.text,
          completed: item.completed,
          deadline: item.dueDate,
          tabName: "Remember",
          isRemember: true,
        }))
    : [];

  // ---- Conferences (optional panel) ----
  const addConferenceCard = (card) => {
    trackConferenceAdded();
    setConferenceCards((prev) => [...prev, { ...card, order: prev.length }]);
  };

  const updateConferenceCard = (cardId, updates) => {
    setConferenceCards((prev) =>
      prev.map((card) =>
        card.id === cardId
          ? { ...card, ...updates, updatedAt: new Date().toISOString() }
          : card,
      ),
    );
  };

  const completeConferenceRemember = (cardId, entryId) => {
    setConferenceCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        const entries = (card.entries || []).map((e) =>
          e.id === entryId ? { ...e, remember: false } : e,
        );
        return { ...card, entries, updatedAt: new Date().toISOString() };
      }),
    );
  };

  const deleteConferenceCard = (cardId) => {
    const idx = conferenceCards.findIndex((x) => x.id === cardId);
    const item = idx >= 0 ? conferenceCards[idx] : null;
    setConferenceCards((prev) => prev.filter((x) => x.id !== cardId));
    if (item)
      pushUndo(`Deleted conference${quoteLabel(item.title || item.text)}`, () =>
        setConferenceCards((prev) =>
          prev.some((x) => x.id === item.id) ? prev : insertAt(prev, idx, item),
        ),
      );
  };

  const deleteAllConferenceCards = () => {
    setConferenceCards([]);
  };

  // Mark a conference edition submitted (from the Reminders panel tick).
  const markConferenceSubmitted = (cardId, entryId) => {
    setConferenceCards((prev) =>
      prev.map((card) => {
        if (card.id !== cardId) return card;
        const entries = (card.entries || []).map((e) =>
          e.id === entryId ? { ...e, submitted: true } : e,
        );
        return { ...card, entries, updatedAt: new Date().toISOString() };
      }),
    );
  };

  const toggleShowConferences = (value) => {
    const next = Boolean(value);
    setShowConferences(next);
    setPanelOrder((prev) => normalizePanelOrder(prev, next, showPublications));
  };

  // Derived view models for Reminders / Remember (only when the panel is on).
  const conferenceReminders = showConferences
    ? conferenceReminderItems(conferenceCards)
    : [];
  const conferenceRemembers = showConferences
    ? conferenceRememberItems(conferenceCards)
    : [];

  // ---- Publications (optional panel) ----
  const addPublication = (pub) => {
    setPublications((prev) => [
      ...prev,
      resolvePublication({ ...pub, order: prev.length }),
    ]);
  };

  const updatePublication = (pubId, updates) => {
    // Re-derive the "current" attempt after every edit: the best attempt (by
    // status preference, then earliest submission date) is promoted to current
    // and the rest drop into history. This is what makes current/history swap
    // automatically when a status or date changes.
    setPublications((prev) =>
      prev.map((p) =>
        p.id === pubId
          ? resolvePublication({
              ...p,
              ...updates,
              updatedAt: new Date().toISOString(),
            })
          : p,
      ),
    );
  };

  const deletePublication = (pubId) => {
    const idx = publications.findIndex((x) => x.id === pubId);
    const item = idx >= 0 ? publications[idx] : null;
    setPublications((prev) => prev.filter((x) => x.id !== pubId));
    if (item)
      pushUndo(
        `Deleted publication${quoteLabel(item.title || item.text)}`,
        () =>
          setPublications((prev) =>
            prev.some((x) => x.id === item.id)
              ? prev
              : insertAt(prev, idx, item),
          ),
      );
  };

  const toggleShowPublications = (value) => {
    const next = Boolean(value);
    setShowPublications(next);
    setPanelOrder((prev) => normalizePanelOrder(prev, showConferences, next));
  };

  // Ticking a publication reminder dismisses it by clearing the date it was
  // built from (the paper itself is untouched).
  const dismissPublicationReminder = (pubId, kind) => {
    setPublications((prev) =>
      prev.map((p) =>
        p.id === pubId
          ? resolvePublication({
              ...p,
              ...(kind === "camera"
                ? { cameraReadyDate: null }
                : { expectedDecisionDate: null }),
              updatedAt: new Date().toISOString(),
            })
          : p,
      ),
    );
  };

  // ---------- Sticky notes ----------
  const addStickyNote = (partial = {}) => {
    const now = new Date().toISOString();
    const z = stickyNotes.reduce((m, n) => Math.max(m, n.z || 0), 0) + 1;
    const note = {
      id: newRecordId(),
      text: "",
      color: "yellow",
      anchor: null,
      x: 0.5,
      y: 0,
      w: 220,
      h: 160,
      minimized: false,
      dueDate: null,
      z,
      createdAt: now,
      updatedAt: now,
      ...partial,
    };
    setStickyNotes((prev) => [...prev, note]);
    return note.id;
  };
  const updateStickyNote = (noteId, updates) => {
    setStickyNotes((prev) =>
      prev.map((n) =>
        n.id === noteId
          ? { ...n, ...updates, updatedAt: new Date().toISOString() }
          : n,
      ),
    );
  };
  const deleteStickyNote = (noteId) => {
    const idx = stickyNotes.findIndex((x) => x.id === noteId);
    const item = idx >= 0 ? stickyNotes[idx] : null;
    setStickyNotes((prev) => prev.filter((x) => x.id !== noteId));
    if (item)
      pushUndo(`Deleted note${quoteLabel(item.title || item.text)}`, () =>
        setStickyNotes((prev) =>
          prev.some((x) => x.id === item.id) ? prev : insertAt(prev, idx, item),
        ),
      );
  };
  // Sticky Notes board: a note sent there takes the next free grid slot and
  // remembers its previous layout in `boardReturn` (JSON) for "Go back".
  const NOTE_CARD_W = BOARD_CARD_W;
  const NOTE_CARD_H = BOARD_CARD_H;
  const NOTE_GAP = BOARD_GAP;
  const boardColumns = () => boardColumnCount();
  const noteLayout = (n) => ({
    anchor: n.anchor || null,
    x: n.x,
    y: n.y,
    w: n.w,
    h: n.h,
    minimized: !!n.minimized,
  });
  const parseBoardReturn = (n) => {
    try {
      const p = n.boardReturn ? JSON.parse(n.boardReturn) : null;
      return p && typeof p === "object" ? p : null;
    } catch (e) {
      return null;
    }
  };
  // Move the given notes onto the board, each into the next free slot.
  const sendNotesToBoard = (ids) => {
    const cols = boardColumns();
    const now = new Date().toISOString();
    const want = new Set(ids);
    setCollapsedPanels((prev) => prev.filter((k) => k !== "stickyNotes"));
    setStickyNotes((prev) => {
      // Notes already parked on the board hold their cells; each arriving note
      // takes the first free cell after them (reading order).
      const occupied = prev.filter(
        (n) => n.anchor === "stickyNotes" && !want.has(n.id),
      );
      let z = prev.reduce((m, n) => Math.max(m, n.z || 0), 0);
      return prev.map((n) => {
        if (!want.has(n.id) || n.anchor === "stickyNotes") return n;
        const sized = {
          ...n,
          w: Math.min(NOTE_CARD_W, boardMaxNoteW(cols)),
          h: NOTE_CARD_H,
          minimized: false,
        };
        const slot = snapToBoard(sized, occupied, cols, 0, 0);
        const placed = {
          x: slot.x,
          y: slot.y,
          w: slot.w,
          h: NOTE_CARD_H,
        };
        occupied.push({ ...sized, ...placed, anchor: "stickyNotes" });
        z += 1;
        return {
          ...n,
          anchor: "stickyNotes",
          ...placed,
          minimized: false,
          z,
          boardReturn: JSON.stringify(noteLayout(n)),
          updatedAt: now,
        };
      });
    });
  };
  // Send notes back to where they were before the board. Notes created on the
  // board (no previous layout) stay put.
  const returnNotesFromBoard = (ids) => {
    const now = new Date().toISOString();
    const want = new Set(ids);
    setStickyNotes((prev) =>
      prev.map((n) => {
        if (!want.has(n.id) || n.anchor !== "stickyNotes") return n;
        const p = parseBoardReturn(n);
        if (!p || p.anchor === "stickyNotes") return n;
        return {
          ...n,
          anchor: p.anchor || null,
          x: Number(p.x) || 0,
          y: Number(p.y) || 0,
          w: Number(p.w) || 220,
          h: Number(p.h) || 160,
          minimized: !!p.minimized,
          boardReturn: null,
          updatedAt: now,
        };
      }),
    );
  };
  const sendStickyNoteToBoard = (noteId) => sendNotesToBoard([noteId]);
  const returnStickyNoteFromBoard = (noteId) => returnNotesFromBoard([noteId]);
  const gatherStickyNotes = () =>
    sendNotesToBoard(
      stickyNotes.filter((n) => n.anchor !== "stickyNotes").map((n) => n.id),
    );
  const restoreStickyNotesLayout = () =>
    returnNotesFromBoard(
      stickyNotes.filter((n) => n.boardReturn).map((n) => n.id),
    );
  // Tidy the board: any note whose stored cell is outside the dashed area, is
  // wider than the board, or sits on another note is re-seated in the nearest
  // free cell. Runs when the board appears and when its width changes, so old
  // layouts (and notes dragged out before this rule existed) heal themselves.
  const normalizeBoardNotes = useCallback(() => {
    if (!document.querySelector("[data-notes-board]")) return;
    const cols = boardColumnCount();
    const maxW = boardMaxNoteW(cols);
    setStickyNotes((prev) => {
      const board = prev.filter((n) => n.anchor === "stickyNotes");
      if (!board.length) return prev;
      const placed = [];
      const fixes = new Map();
      for (const n of board) {
        const w = Math.min(Number(n.w) || BOARD_CARD_W, maxW);
        const slot = snapToBoard({ ...n, w }, placed, cols, n.x, n.y);
        placed.push({ ...n, x: slot.x, y: slot.y, w: slot.w });
        if (
          Math.round(slot.x) !== Math.round(n.x || 0) ||
          Math.round(slot.y) !== Math.round(n.y || 0) ||
          Math.round(slot.w) !== Math.round(Number(n.w) || BOARD_CARD_W)
        )
          fixes.set(n.id, { x: slot.x, y: slot.y, w: Math.round(slot.w) });
      }
      if (!fixes.size) return prev;
      const now = new Date().toISOString();
      return prev.map((n) =>
        fixes.has(n.id) ? { ...n, ...fixes.get(n.id), updatedAt: now } : n,
      );
    });
  }, []);

  const boardNotes = stickyNotes.filter((n) => n.anchor === "stickyNotes");
  const canGatherNotes = stickyNotes.some((n) => n.anchor !== "stickyNotes");
  const canRestoreNotes = boardNotes.some((n) => n.boardReturn);
  // Rows the board must cover: the lowest cell any note occupies (so a note
  // parked in row 3 is always inside the dashed area, not hanging below it).
  const boardRows = Math.max(
    1,
    ...boardNotes.map((n) =>
      Math.ceil(
        ((Number(n.y) || 0) + (n.minimized ? 32 : Number(n.h) || NOTE_CARD_H)) /
          (NOTE_CARD_H + NOTE_GAP),
      ),
    ),
  );
  const boardNoteCount = boardNotes.length;
  useEffect(() => {
    if (!isInitialized || !boardNoteCount) return;
    const run = () => normalizeBoardNotes();
    const t = setTimeout(run, 250); // after the board has laid out
    window.addEventListener("resize", run);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", run);
    };
  }, [isInitialized, boardNoteCount, normalizeBoardNotes]);

  // Notes gathered by the old whole-board "Gather" kept their previous layout
  // in `notesSavedLayout`; move it onto each note's `boardReturn` once.
  useEffect(() => {
    if (!isInitialized || !notesSavedLayout || !stickyNotes.length) return;
    let saved = {};
    try {
      saved = JSON.parse(notesSavedLayout) || {};
    } catch (e) {
      saved = {};
    }
    const now = new Date().toISOString();
    setStickyNotes((prev) =>
      prev.map((n) => {
        const p = saved[n.id];
        if (
          n.anchor !== "stickyNotes" ||
          n.boardReturn ||
          !p ||
          p.anchor === "stickyNotes"
        )
          return n;
        return {
          ...n,
          boardReturn: JSON.stringify(noteLayout(p)),
          updatedAt: now,
        };
      }),
    );
    setNotesSavedLayout(null);
    setNotesGathered(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, notesSavedLayout, stickyNotes.length]);

  // Ticking a note reminder: a whole-note reminder clears the note's due date;
  // a checklist-line reminder ticks that "[ ]" line in the note, which is what
  // makes it drop out of Reminders.
  const dismissStickyNoteReminder = (noteId, lineIndex) => {
    if (typeof lineIndex === "number") {
      const note = stickyNotes.find((n) => n.id === noteId);
      if (!note) return;
      const next = toggleCheckboxLine(note.text || "", lineIndex);
      if (next !== note.text) updateStickyNote(noteId, { text: next });
      return;
    }
    updateStickyNote(noteId, { dueDate: null });
  };
  const stickyNoteReminders = [
    ...stickyNotes
      .filter((n) => n.dueDate)
      .map((n) => ({
        id: `note-${n.id}`,
        noteId: n.id,
        text: (
          (n.text || "").split("\n").find((l) => l.trim()) || "Sticky note"
        ).slice(0, 120),
        completed: false,
        deadline: n.dueDate,
        tabName: "Note",
        isStickyNote: true,
      })),
    // Unticked "[ ] … DD.MM.YYYY" lines inside notes — one reminder each.
    ...stickyNotes.flatMap((n) =>
      datedNoteTasks(n.text).map((t) => ({
        id: `note-${n.id}-line-${t.lineIndex}`,
        noteId: n.id,
        lineIndex: t.lineIndex,
        text: t.text.slice(0, 120),
        completed: false,
        deadline: t.date,
        tabName: "Note",
        isStickyNote: true,
        isNoteTask: true,
      })),
    ),
  ];

  // ---------- Bulk goals (paste a list) ----------
  // One functional update per collection so many goals/sub-goals land at once
  // without stale-closure races.
  const addGoalsBulk = (tabId, items, deadline = null) => {
    const now = new Date().toISOString();
    const newGoals = [];
    const newSubs = [];
    for (const it of items || []) {
      if (!it || !it.text) continue;
      const gid = newRecordId();
      newGoals.push({
        id: gid,
        text: it.text,
        completed: false,
        deadline: deadline || null,
        important: false,
      });
      for (const st of it.subGoals || []) {
        if (!st) continue;
        newSubs.push({
          id: newRecordId(),
          goalId: gid,
          text: st,
          completed: false,
          deadline: deadline || null,
          important: false,
          createdAt: now,
          updatedAt: now,
        });
      }
    }
    if (!newGoals.length) return;
    setTabs((prev) =>
      prev.map((t) =>
        t.id === tabId ? { ...t, goals: [...t.goals, ...newGoals] } : t,
      ),
    );
    if (newSubs.length) setSubGoals((prev) => [...prev, ...newSubs]);
  };

  // Reminders derived from publications (only when the panel is on):
  // expected-decision dates while a paper is under review, and camera-ready
  // deadlines once accepted.
  const publicationReminders = showPublications
    ? publications.flatMap((p) => {
        const items = [];
        if (
          ["drafting", "submitted", "revision", "awaiting-decision"].includes(
            p.status,
          ) &&
          p.expectedDecisionDate
        ) {
          items.push({
            id: `pub-${p.id}-decision`,
            pubId: p.id,
            kind: "decision",
            text: `${p.title} — Decision/Due`,
            completed: false,
            deadline: p.expectedDecisionDate,
            tabName: "Publication",
            isPublication: true,
          });
        }
        if (p.status === "accepted" && p.cameraReadyDate) {
          items.push({
            id: `pub-${p.id}-camera`,
            pubId: p.id,
            kind: "camera",
            text: `${p.title} — camera-ready due`,
            completed: false,
            deadline: p.cameraReadyDate,
            tabName: "Publication",
            isPublication: true,
          });
        }
        return items;
      })
    : [];

  // Sub-goal management
  const addSubGoal = (goalId, text, deadline = null) => {
    const newSubGoal = {
      id: newRecordId(),
      goalId,
      text,
      completed: false,
      deadline: deadline || null,
      important: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSubGoals([...subGoals, newSubGoal]);
  };

  const toggleSubGoal = (subGoalId) => {
    setSubGoals(
      subGoals.map((subGoal) =>
        subGoal.id === subGoalId
          ? {
              ...subGoal,
              completed: !subGoal.completed,
              completedAt: !subGoal.completed ? new Date().toISOString() : null,
              updatedAt: new Date().toISOString(),
            }
          : subGoal,
      ),
    );
  };

  const deleteSubGoal = (subGoalId) => {
    const idx = subGoals.findIndex((sg) => sg.id === subGoalId);
    const item = idx >= 0 ? subGoals[idx] : null;
    setSubGoals(subGoals.filter((subGoal) => subGoal.id !== subGoalId));
    if (item)
      pushUndo(`Deleted sub-goal “${String(item.text).slice(0, 40)}”`, () =>
        setSubGoals((prev) =>
          prev.some((x) => x.id === item.id) ? prev : insertAt(prev, idx, item),
        ),
      );
  };

  const editSubGoal = (subGoalId, newText) => {
    setSubGoals(
      subGoals.map((subGoal) =>
        subGoal.id === subGoalId
          ? {
              ...subGoal,
              text: newText,
              updatedAt: new Date().toISOString(),
            }
          : subGoal,
      ),
    );
  };

  const updateSubGoalDeadline = (subGoalId, deadline) => {
    setSubGoals(
      subGoals.map((subGoal) =>
        subGoal.id === subGoalId
          ? {
              ...subGoal,
              deadline,
              updatedAt: new Date().toISOString(),
            }
          : subGoal,
      ),
    );
  };

  const toggleSubGoalImportance = (subGoalId) => {
    setSubGoals(
      subGoals.map((subGoal) =>
        subGoal.id === subGoalId
          ? {
              ...subGoal,
              important: !subGoal.important,
              updatedAt: new Date().toISOString(),
            }
          : subGoal,
      ),
    );
  };

  // Helper function to get sub-goals for a specific goal
  const getSubGoalsForGoal = (goalId) => {
    return subGoals.filter((subGoal) => subGoal.goalId === goalId);
  };

  // Helper function to check if all sub-goals are completed
  const areAllSubGoalsCompleted = (goalId) => {
    const goalSubGoals = getSubGoalsForGoal(goalId);
    if (goalSubGoals.length === 0) return true; // No sub-goals means can complete
    return goalSubGoals.every((subGoal) => subGoal.completed);
  };

  // Remember list management
  const addRememberItem = (text, dueDate = null, recurrence = null) => {
    if (!text || !text.trim()) return;
    trackRememberAdded();
    const isRec =
      recurrence &&
      Array.isArray(recurrence.weekdays) &&
      recurrence.weekdays.length > 0 &&
      recurrence.endDate;
    const newItem = {
      id: newRecordId(),
      text: text.trim(),
      completed: false,
      // A recurring item never carries a single due date.
      dueDate: isRec ? null : dueDate || null,
      ...(isRec
        ? {
            recurrence: {
              weekdays: [...recurrence.weekdays].sort((a, b) => a - b),
              endDate: recurrence.endDate,
            },
            completedDates: [],
          }
        : {}),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRememberItems((prev) => [...prev, newItem]);
  };

  // Mark a single occurrence (by date) of a recurring item complete / not.
  const toggleRecurringOccurrence = (itemId, dateStr) => {
    if (!dateStr) return;
    setRememberItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const dates = new Set(item.completedDates || []);
        if (dates.has(dateStr)) dates.delete(dateStr);
        else dates.add(dateStr);
        return {
          ...item,
          completedDates: Array.from(dates),
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  // End the whole recurring series (no more occurrences appear anywhere).
  const completeRecurringSeries = (itemId) => {
    setRememberItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, completed: true, updatedAt: new Date().toISOString() }
          : item,
      ),
    );
  };

  // Edit a recurring item's weekdays / end date (today-and-future only;
  // past completedDates are never rewritten). Passing null re-activates a
  // series that had been ended.
  const updateRememberRecurrence = (itemId, recurrence) => {
    setRememberItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        if (
          !recurrence ||
          !Array.isArray(recurrence.weekdays) ||
          recurrence.weekdays.length === 0 ||
          !recurrence.endDate
        ) {
          return item;
        }
        return {
          ...item,
          completed: false,
          recurrence: {
            weekdays: [...recurrence.weekdays].sort((a, b) => a - b),
            endDate: recurrence.endDate,
          },
          updatedAt: new Date().toISOString(),
        };
      }),
    );
  };

  const toggleRememberItem = (itemId) => {
    setRememberItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              completed: !item.completed,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  const editRememberItem = (itemId, newText) => {
    if (!newText || !newText.trim()) return;
    setRememberItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              text: newText.trim(),
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  const deleteRememberItem = (itemId) => {
    const idx = rememberItems.findIndex((x) => x.id === itemId);
    const item = idx >= 0 ? rememberItems[idx] : null;
    setRememberItems((prev) => prev.filter((x) => x.id !== itemId));
    if (item)
      pushUndo(`Deleted reminder${quoteLabel(item.title || item.text)}`, () =>
        setRememberItems((prev) =>
          prev.some((x) => x.id === item.id) ? prev : insertAt(prev, idx, item),
        ),
      );
  };

  const updateRememberItemDueDate = (itemId, dueDate) => {
    setRememberItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              dueDate: dueDate || null,
              updatedAt: new Date().toISOString(),
            }
          : item,
      ),
    );
  };

  // Completion wrappers: un-completing toggles instantly. Completing an item
  // does NOT mark it complete yet -- it opens the "Add to Remember?" prompt and
  // the completion is only committed when the user explicitly chooses Yes/No.
  // Closing the prompt (X / backdrop) cancels and leaves the item unmarked.
  const applyPendingCompletion = (target) => {
    if (!target) return;
    if (target.type === "goal") {
      toggleGoal(target.tabId, target.goalId);
    } else if (target.type === "subgoal") {
      toggleSubGoal(target.subGoalId);
    }
  };

  const requestToggleGoal = (tabId, goalId) => {
    let targetGoal = null;
    for (const tab of tabs) {
      if (tab.id === tabId && tab.goals) {
        targetGoal = tab.goals.find((g) => g.id === goalId);
        break;
      }
    }
    const willComplete = targetGoal && !targetGoal.completed;
    if (willComplete && targetGoal) {
      setRememberPrompt({
        open: true,
        name: targetGoal.text,
        target: { type: "goal", tabId, goalId },
      });
    } else {
      toggleGoal(tabId, goalId);
    }
  };

  const requestToggleSubGoal = (subGoalId) => {
    const targetSubGoal = subGoals.find((sg) => sg.id === subGoalId);
    const willComplete = targetSubGoal && !targetSubGoal.completed;
    if (willComplete && targetSubGoal) {
      setRememberPrompt({
        open: true,
        name: targetSubGoal.text,
        target: { type: "subgoal", subGoalId },
      });
    } else {
      toggleSubGoal(subGoalId);
    }
  };

  // Yes: mark complete AND add to Remember.
  const confirmRememberPrompt = (dueDate) => {
    applyPendingCompletion(rememberPrompt.target);
    addRememberItem(rememberPrompt.name, dueDate);
    setRememberPrompt({ open: false, name: "", target: null });
  };

  // No: mark complete only (do not add to Remember).
  const declineRememberPrompt = () => {
    applyPendingCompletion(rememberPrompt.target);
    setRememberPrompt({ open: false, name: "", target: null });
  };

  // Close (X / backdrop): cancel entirely, leave the item unmarked.
  const closeRememberPrompt = () => {
    setRememberPrompt({ open: false, name: "", target: null });
  };

  // Settings management
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    // Clear completed days if start date or number of days changed significantly
    if (
      newSettings.startDate !== settings.startDate ||
      newSettings.numberOfDays !== settings.numberOfDays
    ) {
      const shouldReset = window.confirm(
        "Changing the start date or number of days will reset your progress. Continue?",
      );
      if (shouldReset) {
        setCompletedDays(new Set());
        setPartialDays(new Set());
      }
    }
  };

  const resetAllData = async () => {
    setCompletedDays(new Set());
    setPartialDays(new Set());
    setRememberItems([]);
    const initialTabs = createInitialTabs();
    setTabs(initialTabs);
    setActiveTabId(initialTabs[0].id);
    setSettings((prev) => ({
      challengeName: "My Challenge",
      numberOfDays: 31,
      startDate: formatDate(new Date()),
      userName: prev.userName,
      passkey: prev.passkey,
    }));
    // No explicit cloud write: the edit-time effect diffs this against the prior
    // state and enqueues the right tombstones + upserts, which flush to the
    // cloud. Every applyChanges snapshots first, so a reset is recoverable from
    // version history. The local backup is rewritten by the same effect.
  };

  // Handle migration from localStorage to Google Sheets
  const handleMigration = async () => {
    try {
      setIsLoading(true);
      await migrateFromLocalStorage();
      setMigrationPrompt(false);
      setUseGoogleSheets(true);
      setError(null);
      // Reload data from Google Sheets
      const data = await fetchAllData();
      if (data) {
        loadLocalData(data);
      }
    } catch (error) {
      console.error("Migration error:", error);
      setError("Failed to migrate data. Continuing with localStorage.");
      setUseGoogleSheets(false);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissMigration = () => {
    setMigrationPrompt(false);
    setUseGoogleSheets(false);
  };

  const exportData = () => {
    // Create tabs with subGoals nested under each goal
    const tabsWithSubGoals = tabs.map((tab) => ({
      ...tab,
      goals: tab.goals.map((goal) => ({
        ...goal,
        subGoals: subGoals.filter((sg) => sg.goalId === goal.id),
      })),
    }));

    const dataToExport = {
      settings,
      completedDays: Array.from(completedDays),
      partialDays: Array.from(partialDays),
      tabs: tabsWithSubGoals,
      activeTabId,
      subGoals, // Also keep flat array for backward compatibility
      clipboardCards,
      quickLinkCards,
      conferenceCards,
      showConferences,
      conferenceSortMode,
      publications,
      showPublications,
      publicationSortMode,
      rememberItems,
      showRememberInReminders,
      stickyNotes,
      panelOrder: normalizePanelOrder(
        panelOrder,
        showConferences,
        showPublications,
      ),
      lastModified,
      exportDate: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `xunzhine-backup-${formatDate(new Date())}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (data) => {
    if (data.settings) {
      setSettings((prev) => ({
        ...data.settings,
        userName: prev.userName,
        passkey: prev.passkey,
      }));
    }
    if (data.completedDays) setCompletedDays(new Set(data.completedDays));
    if (data.partialDays) setPartialDays(new Set(data.partialDays));
    if (data.clipboardCards)
      setClipboardCards(sortByOrder(data.clipboardCards));
    if (data.quickLinkCards)
      setQuickLinkCards(sortByOrder(data.quickLinkCards));
    if (data.conferenceCards)
      setConferenceCards(sortByOrder(data.conferenceCards));
    const conferencesOn =
      typeof data.showConferences === "boolean"
        ? data.showConferences
        : showConferences;
    if (typeof data.showConferences === "boolean")
      setShowConferences(data.showConferences);
    if (data.conferenceSortMode)
      setConferenceSortMode(
        normalizeConferenceSortMode(data.conferenceSortMode),
      );
    if (data.publications)
      setPublications(sortByOrder(data.publications).map(resolvePublication));
    if (Array.isArray(data.stickyNotes)) setStickyNotes(data.stickyNotes);
    const publicationsOn =
      typeof data.showPublications === "boolean"
        ? data.showPublications
        : showPublications;
    if (typeof data.showPublications === "boolean")
      setShowPublications(data.showPublications);
    if (typeof data.showRememberInReminders === "boolean")
      setShowRememberInReminders(data.showRememberInReminders);
    if (data.publicationSortMode)
      setPublicationSortMode(
        normalizePublicationSortMode(data.publicationSortMode),
      );
    if (data.subGoals) setSubGoals(data.subGoals);
    if (data.rememberItems) setRememberItems(sortByOrder(data.rememberItems));
    if (data.panelOrder)
      setPanelOrder(
        normalizePanelOrder(data.panelOrder, conferencesOn, publicationsOn),
      );
    if (data.lastModified) setLastModified(data.lastModified);

    // Handle both old and new data structures
    if (data.tabs && Array.isArray(data.tabs) && data.tabs.length > 0) {
      setTabs(data.tabs);
      setActiveTabId(data.activeTabId || data.tabs[0].id);
    } else if (data.goals && Array.isArray(data.goals)) {
      // Migrate old structure
      const migratedTabs = createInitialTabs(data.goals);
      setTabs(migratedTabs);
      setActiveTabId(migratedTabs[0].id);
    }
  };

  // Handle logout
  const handleLogout = () => {
    // Revoke the server session (best effort) and clear auth from localStorage
    cloudLogout();
    localStorage.removeItem("xDayAuth");
    // Reset per-session sync state (cursor, outbox, bootstrap flag) and the
    // in-memory baseline so a different account starts clean.
    clearSyncState();
    prevStateRef.current = null;
    suppressDiffRef.current = false;
    setIsInitialized(false);

    // Reset states
    setShowLogin(true);
    setUseGoogleSheets(false);
    setStorageMode("");
    setSettings({
      challengeName: "My Challenge",
      numberOfDays: 31,
      startDate: formatDate(new Date()),
      userName: "",
      passkey: "",
    });

    // Clear sync states
    setIsOnline(navigator.onLine);
    setIsSynced(true);
    setLastSyncTime(null);
    setHasUnsyncedChanges(false);
    setLastModified(null);

    // Optionally clear all data (uncomment if you want to clear on logout)
    // const initialTabs = createInitialTabs();
    // setTabs(initialTabs);
    // setActiveTabId(initialTabs[0].id);
    // setCompletedDays(new Set());
    // setClipboardCards([]);
    // setSubGoals([]);
  };

  // Handle cleanup duplicates
  const handleCleanupDuplicates = async () => {
    if (!useGoogleSheets) {
      return {
        success: false,
        message: "Cleanup is only available for cloud storage users",
      };
    }

    try {
      const result = await cleanupDuplicates();

      // If cleanup was successful, reload data to reflect changes
      if (result.success) {
        await loadData(true);
      }

      return result;
    } catch (error) {
      console.error("Error cleaning duplicates:", error);
      return {
        success: false,
        message: error.message || "Failed to cleanup duplicates",
      };
    }
  };

  // ---------- AI companion ----------
  // Cloud accounts only (keys live encrypted in Convex). The companion never
  // acts on its own: it reads the panels allowed in Settings when asked, and
  // every change it proposes is applied here only after the user confirms.
  const aiName = (aiSettings && aiSettings.assistantName) || "xyz Ai";
  const aiConfigured = !!(aiSettings && aiSettings.configured);
  const toggleAiAwake = () => {
    setAiAwake((v) => {
      try {
        localStorage.setItem("xz-ai-awake", v ? "0" : "1");
      } catch (e) {
        /* ignore */
      }
      return !v;
    });
  };
  const openAiForPanel = (panelKey) => {
    setAiFocus(PANEL_SCOPE[panelKey] || null);
    setAiAwake(true);
    try {
      localStorage.setItem("xz-ai-awake", "1");
    } catch (e) {
      /* ignore */
    }
    setAiOpenSignal((n) => n + 1);
  };
  const aiPanelHandler = useGoogleSheets ? openAiForPanel : undefined;

  const pickReminder = (r, kind) => ({
    kind,
    text: r.text || r.title || r.label || "",
    due: r.deadline || r.dueDate || r.date || null,
  });
  // Everything the companion may read, in the app's own shapes (context.js
  // trims it to the allowed scopes and a token-friendly size).
  const getAiState = () => ({
    settings,
    today: formatDate(new Date()),
    completedDays: Array.from(completedDays),
    partialDays: Array.from(partialDays),
    challengeDates: challengeDates.map((d) => ({ date: d.date, isToday: d.isToday })),
    streak: currentStreak,
    bestStreak,
    tabs,
    subGoals,
    stickyNotes,
    rememberItems,
    clipboardCards,
    quickLinkCards,
    conferenceCards: showConferences ? conferenceCards : [],
    publications: showPublications ? publications : [],
    reminders: [
      ...tabs.flatMap((t) =>
        t.goals.filter((g) => g.deadline && !g.completed).map((g) => pickReminder(g, "goal")),
      ),
      ...subGoals.filter((s) => s.deadline && !s.completed).map((s) => pickReminder(s, "sub-goal")),
      ...rememberReminderItems.map((r) => pickReminder(r, "remember")),
      ...quickLinkReminderItems.map((r) => pickReminder(r, "quick link")),
      ...stickyNoteReminders.map((r) => pickReminder(r, "sticky note")),
      ...conferenceReminders.map((r) => pickReminder(r, "conference")),
      ...publicationReminders.map((r) => pickReminder(r, "publication")),
    ]
      .filter((r) => r.due)
      .sort((a, b) => String(a.due).localeCompare(String(b.due)))
      .slice(0, 80),
  });

  // Resolve "id or text" references the model uses in its proposals.
  const sameRef = (ref, id, text) => {
    const r = String(ref ?? "").trim().toLowerCase();
    if (!r) return false;
    if (String(id).toLowerCase() === r) return true;
    const t = String(text || "").trim().toLowerCase();
    return !!t && (t === r || t.includes(r) || r.includes(t));
  };
  const findGoalRef = (ref) => {
    for (const tab of tabs) {
      const g = tab.goals.find((x) => String(x.id) === String(ref));
      if (g) return { tab, goal: g };
    }
    for (const tab of tabs) {
      const g = tab.goals.find((x) => sameRef(ref, x.id, x.text));
      if (g) return { tab, goal: g };
    }
    return null;
  };
  const findIn = (arr, ref, textKey = "text") =>
    arr.find((x) => String(x.id) === String(ref)) ||
    arr.find((x) => sameRef(ref, x.id, x[textKey])) ||
    null;
  const setDayStatus = (date, status) => {
    const nc = new Set(completedDays);
    const np = new Set(partialDays);
    nc.delete(date);
    np.delete(date);
    if (status === "complete") nc.add(date);
    else if (status === "partial") np.add(date);
    setCompletedDays(nc);
    setPartialDays(np);
  };
  const applyAiAction = (a) => {
    const ok = (message) => ({ ok: true, message });
    const fail = (message) => ({ ok: false, message });
    const nowIso = () => new Date().toISOString();
    switch (a.type) {
      case "add_goal": {
        if (!a.text) return fail("Missing goal text.");
        const tab =
          (a.tab && tabs.find((t) => String(t.id) === String(a.tab) || sameRef(a.tab, t.id, t.name))) ||
          tabs.find((t) => t.id === activeTabId) ||
          tabs[0];
        if (!tab) return fail("No goals tab exists yet.");
        addGoal(tab.id, String(a.text), a.deadline || null);
        return ok(`Added to “${tab.name}”.`);
      }
      case "add_sub_goal": {
        const f = findGoalRef(a.goal);
        if (!f) return fail("Could not find that goal.");
        if (!a.text) return fail("Missing sub-goal text.");
        addSubGoal(f.goal.id, String(a.text), a.deadline || null);
        return ok(`Sub-goal added under “${f.goal.text}”.`);
      }
      case "complete_goal":
      case "uncomplete_goal": {
        const f = findGoalRef(a.goal);
        if (!f) return fail("Could not find that goal.");
        const want = a.type === "complete_goal";
        if (!!f.goal.completed === want) return ok("Already in that state.");
        toggleGoal(f.tab.id, f.goal.id);
        return ok(want ? "Goal marked complete." : "Goal reopened.");
      }
      case "complete_sub_goal":
      case "uncomplete_sub_goal": {
        const sg = findIn(subGoals, a.subGoal);
        if (!sg) return fail("Could not find that sub-goal.");
        const want = a.type === "complete_sub_goal";
        if (!!sg.completed === want) return ok("Already in that state.");
        toggleSubGoal(sg.id);
        return ok(want ? "Sub-goal marked complete." : "Sub-goal reopened.");
      }
      case "set_goal_deadline": {
        const f = findGoalRef(a.goal);
        if (!f) return fail("Could not find that goal.");
        updateGoalDeadline(f.tab.id, f.goal.id, a.deadline || null);
        return ok(a.deadline ? `Deadline set to ${a.deadline}.` : "Deadline cleared.");
      }
      case "delete_goal": {
        const f = findGoalRef(a.goal);
        if (!f) return fail("Could not find that goal.");
        deleteGoal(f.tab.id, f.goal.id);
        return ok("Goal deleted (Undo is available).");
      }
      case "delete_sub_goal": {
        const sg = findIn(subGoals, a.subGoal);
        if (!sg) return fail("Could not find that sub-goal.");
        deleteSubGoal(sg.id);
        return ok("Sub-goal deleted.");
      }
      case "add_sticky_note": {
        if (!a.text) return fail("Missing note text.");
        const colors = ["yellow", "pink", "blue", "green", "purple"];
        addStickyNote({
          text: String(a.text),
          color: colors.includes(a.color) ? a.color : "yellow",
          dueDate: a.dueDate || null,
        });
        return ok("Sticky note added.");
      }
      case "update_sticky_note": {
        const n = findIn(stickyNotes, a.note);
        if (!n) return fail("Could not find that note.");
        updateStickyNote(n.id, { text: String(a.text || "") });
        return ok("Note updated.");
      }
      case "delete_sticky_note": {
        const n = findIn(stickyNotes, a.note);
        if (!n) return fail("Could not find that note.");
        deleteStickyNote(n.id);
        return ok("Note deleted (Undo is available).");
      }
      case "add_remember": {
        if (!a.text) return fail("Missing text.");
        addRememberItem(String(a.text), a.dueDate || null);
        return ok("Added to Remember.");
      }
      case "complete_remember": {
        const it = findIn(rememberItems, a.item);
        if (!it) return fail("Could not find that item.");
        if (it.completed) return ok("Already ticked.");
        toggleRememberItem(it.id);
        return ok("Ticked.");
      }
      case "delete_remember": {
        const it = findIn(rememberItems, a.item);
        if (!it) return fail("Could not find that item.");
        deleteRememberItem(it.id);
        return ok("Deleted.");
      }
      case "add_clipboard_card": {
        if (!a.title && !a.content) return fail("Missing card content.");
        addClipboardCard({
          id: newRecordId(),
          title: String(a.title || "Untitled"),
          content: String(a.content || ""),
          createdAt: nowIso(),
          updatedAt: nowIso(),
        });
        return ok("Clipboard card added.");
      }
      case "delete_clipboard_card": {
        const c = findIn(clipboardCards, a.card, "title");
        if (!c) return fail("Could not find that card.");
        deleteClipboardCard(c.id);
        return ok("Card deleted.");
      }
      case "add_quick_link_card": {
        const links = (Array.isArray(a.links) ? a.links : [])
          .filter((l) => l && l.url)
          .map((l) => ({
            id: newRecordId(),
            url: String(l.url),
            description: String(l.label || l.description || ""),
            completed: false,
          }));
        if (!links.length) return fail("No links given.");
        addQuickLinkCard({
          id: newRecordId(),
          title: String(a.title || "Links"),
          links,
          dueDate: a.dueDate || null,
          remember: false,
          completed: false,
          createdAt: nowIso(),
          updatedAt: nowIso(),
        });
        return ok("Quick-link card added.");
      }
      case "mark_day": {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(a.date || ""))) return fail("Bad date.");
        if (!challengeDates.some((d) => d.date === a.date)) return fail("That day is not part of the challenge.");
        const st = ["complete", "partial", "none"].includes(a.status) ? a.status : "complete";
        setDayStatus(a.date, st);
        return ok(`Marked ${a.date} as ${st}.`);
      }
      default:
        return fail(`Unknown action “${a.type}”.`);
    }
  };

  // ---------- Stage 4: focus mode, shortcuts, palette, exports ----------
  // Latest "toggle today" action via a ref so the keyboard effect doesn't have
  // to re-subscribe on every render (toggleDay is recreated each render).
  const toggleTodayRef = useRef(() => {});
  toggleTodayRef.current = () => {
    if (todayEntry) toggleDay(todayEntry.date);
  };

  useEffect(() => {
    const html = document.documentElement;
    if (focusMode) {
      html.dataset.focus = "1";
      const el = document.querySelector('[data-panel-key="goals"]');
      if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
    } else {
      delete html.dataset.focus;
    }
    return () => {
      delete html.dataset.focus;
    };
  }, [focusMode]);

  useEffect(() => {
    const isTyping = (t) =>
      t &&
      (t.tagName === "INPUT" ||
        t.tagName === "TEXTAREA" ||
        t.tagName === "SELECT" ||
        t.isContentEditable);
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (e.key === "Escape") {
        if (focusMode && !paletteOpen) setFocusMode(false);
        return;
      }
      if (paletteOpen || isTyping(e.target) || mod || e.altKey) return;
      if (showSettings || showConflictModal || weeklyOpen) return;
      switch (e.key) {
        case "n":
        case "N":
          e.preventDefault();
          focusGoalInput();
          break;
        case "t":
        case "T":
          e.preventDefault();
          toggleTodayRef.current();
          break;
        case "/": {
          e.preventDefault();
          const el = document.querySelector(
            'input[placeholder="Search goals..."]',
          );
          if (el) {
            el.scrollIntoView({ block: "center", behavior: "smooth" });
            setTimeout(() => el.focus(), 250);
          }
          break;
        }
        case "f":
        case "F":
          e.preventDefault();
          setFocusMode((v) => !v);
          break;
        case "w":
        case "W":
          e.preventDefault();
          setWeeklyOpen(true);
          break;
        case "?":
          e.preventDefault();
          setPaletteOpen(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusMode, paletteOpen, showSettings, showConflictModal, weeklyOpen]);

  const collectExportState = () => ({
    settings,
    completedDays: Array.from(completedDays),
    partialDays: Array.from(partialDays),
    tabs,
    subGoals,
    rememberItems,
    clipboardCards,
    quickLinkCards,
    stickyNotes,
    conferenceCards,
    publications,
    showConferences,
    showPublications,
    stats: {
      completed: completedCount,
      total: totalDays,
      streak: currentStreak,
      bestStreak,
      missedDays,
    },
  });
  const exportMarkdown = () =>
    downloadBlob(
      new Blob([buildMarkdown(collectExportState())], {
        type: "text/markdown",
      }),
      `xunzhine-${formatDate(new Date())}.md`,
    );
  const exportCsv = () =>
    downloadBlob(
      new Blob([buildCsv(collectExportState())], { type: "text/csv" }),
      `xunzhine-${formatDate(new Date())}.csv`,
    );
  const shareProgressCard = async () => {
    const cs = getComputedStyle(document.documentElement);
    const rgb = (v) => {
      const t = cs.getPropertyValue(v).trim();
      return t ? `rgb(${t})` : null;
    };
    const blob = await renderShareCard({
      title: settings.challengeName,
      userName: settings.userName,
      completed: completedCount,
      total: totalDays,
      streak: currentStreak,
      bestStreak,
      days: challengeDates.map((d) => ({
        status: completedDays.has(d.date)
          ? "done"
          : partialDays.has(d.date)
            ? "partial"
            : d.isPast
              ? "missed"
              : "future",
      })),
      colors: {
        bg: cs.getPropertyValue("--app-bg-base").trim() || "#0b1220",
        a: cs.getPropertyValue("--brand-a").trim() || "#facc15",
        b: cs.getPropertyValue("--brand-b").trim() || "#22d3ee",
        text: rgb("--c-gray-100") || "#f1f5f9",
        muted: rgb("--c-gray-400") || "#94a3b8",
        future: rgb("--c-gray-600") || "#2a3752",
      },
    });
    if (blob)
      downloadBlob(blob, `xunzhine-progress-${formatDate(new Date())}.png`);
  };

  // Weekly review stats (last 7 full days before today).
  const weeklyStats = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 7; i >= 1; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = formatDate(d);
      const inChallenge = challengeDates.some((c) => c.date === key);
      days.push({
        date: key,
        label: d.toLocaleDateString(undefined, { weekday: "short" }),
        status: !inChallenge
          ? "outside"
          : completedDays.has(key)
            ? "done"
            : partialDays.has(key)
              ? "partial"
              : "missed",
      });
    }
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const goalsDone = tabs
      .flatMap((t) => t.goals || [])
      .filter(
        (g) =>
          g.completed && g.completedAt && new Date(g.completedAt) >= weekAgo,
      ).length;
    return {
      days,
      done: days.filter((d) => d.status === "done").length,
      partial: days.filter((d) => d.status === "partial").length,
      missed: days.filter((d) => d.status === "missed").length,
      goalsDone,
      streak: currentStreak,
      bestStreak,
      notes: stickyNotes.length,
      weekKey: formatDate(weekAgo),
    };
  })();
  const isMonday = new Date().getDay() === 1;

  const buildPaletteActions = () => {
    const acts = [];
    document
      .querySelectorAll("[data-panel-section][data-panel-label]")
      .forEach((el) => {
        acts.push({
          id: `go-${el.dataset.panelKey || el.dataset.panelLabel}`,
          group: "Go to",
          label: el.dataset.panelLabel,
          icon: IcNavigation,
          run: () => {
            const top = el.getBoundingClientRect().top + window.scrollY - 64;
            window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
          },
        });
      });
    acts.push({
      id: "add-goal",
      group: "Actions",
      label: "Add a goal",
      hint: "N",
      icon: IcTarget,
      run: focusGoalInput,
    });
    acts.push({
      id: "mark-today",
      group: "Actions",
      label:
        todayStatus === "none"
          ? "Mark today done"
          : todayStatus === "done"
            ? "Mark today partial"
            : "Clear today",
      hint: "T",
      icon: IcCheck,
      run: () => todayEntry && toggleDay(todayEntry.date),
    });
    acts.push({
      id: "new-note",
      group: "Actions",
      label: "New sticky note",
      icon: IcStickyNote,
      run: () => {
        const el = document.querySelector('[data-panel-key="goals"]');
        addStickyNote({
          anchor: el ? "goals" : null,
          x: el ? 24 : 0.5,
          y: 24,
          z: stickyNotes.reduce((m, n) => Math.max(m, n.z || 0), 0) + 1,
        });
      },
    });
    if (stickyNotes.length) {
      acts.push({
        id: "notes-toggle",
        group: "Actions",
        label: notesHidden ? "Show sticky notes" : "Hide sticky notes",
        icon: IcStickyNote,
        run: () => setNotesHidden(!notesHidden),
      });
      if (canGatherNotes)
        acts.push({
          id: "notes-gather",
          group: "Actions",
          label: "Gather all sticky notes onto the board",
          icon: IcLayoutGrid,
          keywords: "reset tidy lost note board",
          run: () => {
            setNotesHidden(false);
            gatherStickyNotes();
          },
        });
      if (canRestoreNotes)
        acts.push({
          id: "notes-restore",
          group: "Actions",
          label: "Restore all sticky notes from the board",
          icon: IcRotateCcw,
          keywords: "notes board undo gather go back",
          run: () => {
            setNotesHidden(false);
            restoreStickyNotesLayout();
          },
        });
    }
    acts.push({
      id: "focus",
      group: "Actions",
      label: focusMode ? "Exit focus mode" : "Focus mode",
      hint: "F",
      icon: IcFocus,
      run: () => setFocusMode((v) => !v),
    });
    acts.push({
      id: "weekly",
      group: "Actions",
      label: "Weekly review",
      hint: "W",
      icon: IcCalendarDays,
      run: () => setWeeklyOpen(true),
    });
    acts.push({
      id: "calendar-toggle",
      group: "Actions",
      label: calendarCollapsed ? "Expand calendar" : "Minimize calendar",
      icon: IcCalendarDays,
      run: () => setCalendarCollapsed((v) => !v),
    });
    acts.push({
      id: "settings",
      group: "Actions",
      label: "Open settings",
      icon: IcSettings,
      run: () => setShowSettings(true),
    });
    acts.push({
      id: "theme-system",
      group: "Appearance",
      label: "Theme: follow system",
      icon: IcMonitor,
      run: () => setTheme("system"),
    });
    acts.push({
      id: "theme-light",
      group: "Appearance",
      label: "Theme: light",
      icon: IcSun,
      run: () => setTheme("light"),
    });
    acts.push({
      id: "theme-dark",
      group: "Appearance",
      label: "Theme: dark",
      icon: IcMoon,
      run: () => setTheme("dark"),
    });
    (skins || []).forEach((sk) =>
      acts.push({
        id: `skin-${sk.id}`,
        group: "Appearance",
        label: `Skin: ${sk.name}`,
        icon: IcPalette,
        keywords: "skin theme look",
        run: () => setSkin(sk.id),
      }),
    );
    acts.push({
      id: "rail-side",
      group: "Appearance",
      label:
        railSide === "left"
          ? "Panel navigator: move to right"
          : "Panel navigator: move to left",
      icon: railSide === "left" ? IcPanelRight : IcPanelLeft,
      run: () => setRailSide(railSide === "left" ? "right" : "left"),
    });
    [
      ["reminders", "Reminders"],
      ["goals", "Goals"],
      ["clipboard", "Clipboard"],
      ["publications", "Publications"],
      ["conferences", "Conferences"],
      ["stickyNotes", "Sticky Notes"],
    ].forEach(([k, name]) =>
      acts.push({
        id: `collapse-${k}`,
        group: "Actions",
        label: `${isCollapsed(k) ? "Expand" : "Minimize"} ${name} panel`,
        icon: IcCalendarDays,
        run: () => togglePanelCollapsed(k),
      }),
    );
    acts.push({
      id: "top",
      group: "Go to",
      label: "Back to top",
      icon: IcNavigation,
      run: () => window.scrollTo({ top: 0, behavior: "smooth" }),
    });
    acts.push({
      id: "ambient",
      group: "Appearance",
      label: ambientEffects
        ? "Ambient effects: turn off"
        : "Ambient effects: turn on",
      icon: IcSparkles,
      run: () => setAmbientEffects(!ambientEffects),
    });
    acts.push({
      id: "export-json",
      group: "Export",
      label: "Export backup (JSON)",
      icon: IcDownload,
      run: exportData,
    });
    acts.push({
      id: "export-md",
      group: "Export",
      label: "Export as Markdown",
      icon: IcDownload,
      run: exportMarkdown,
    });
    acts.push({
      id: "export-csv",
      group: "Export",
      label: "Export as CSV",
      icon: IcDownload,
      run: exportCsv,
    });
    acts.push({
      id: "share",
      group: "Export",
      label: "Share progress card (PNG)",
      icon: IcShare,
      run: shareProgressCard,
    });
    return acts;
  };

  return (
    <div className="min-h-screen flex flex-col relative isolate">
      <AmbientBackground />
      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          onLogin={handleLogin}
          onLocalMode={enterLocalMode}
          onRecover={isConvexConfigured() ? handleRecover : null}
          error={loginError}
          busy={loginBusy}
        />
      )}

      <Header
        onSettingsClick={() => setShowSettings(true)}
        isCloud={useGoogleSheets}
        stats={
          !isLoading && !showLogin
            ? {
                completed: completedCount,
                total: totalDays,
                streak: currentStreak,
                bestStreak,
                missedDays,
              }
            : null
        }
        todayStatus={todayStatus}
        onMarkToday={() => todayEntry && toggleDay(todayEntry.date)}
        aiName={aiName}
        aiAwake={aiAwake}
        onAiToggle={!isLoading && !showLogin && useGoogleSheets ? toggleAiAwake : null}
      />

      {/* Progress strip: glued under the header, sticks to the top when scrolled */}
      {!isLoading && !showLogin && (
        <ProgressBar completed={completedCount} total={totalDays} />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-cyan-400 mb-4"></div>
            <p className="text-cyan-300 text-xl">Loading your challenge...</p>
          </div>
        </div>
      )}

      {/* Migration Prompt */}
      {migrationPrompt && !isLoading && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-3xl p-8 max-w-md border-2 border-cyan-500/30">
            <h3 className="text-2xl font-bold text-cyan-400 mb-4">
              Migrate to Cloud?
            </h3>
            <p className="text-cyan-200 mb-6">
              We found data in your browser's localStorage. Would you like to
              migrate it to the cloud for better sync and backup?
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleMigration}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold py-3 px-6 rounded-xl transition-all"
              >
                Yes, Migrate
              </button>
              <button
                onClick={dismissMigration}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-all"
              >
                No, Keep Local
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && !isLoading && (
        <div className="mx-4 mt-4">
          <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-4 text-yellow-300">
            <p className="font-semibold">⚠️ {error}</p>
          </div>
        </div>
      )}

      {/* Sync upgrade notice (non-error, dismissible) */}
      {syncNotice && !isLoading && (
        <div className="mx-4 mt-4">
          <div className="bg-cyan-500/15 border-2 border-cyan-500/40 rounded-xl p-4 text-cyan-200 flex items-start justify-between gap-4">
            <p className="font-medium">ℹ️ {syncNotice}</p>
            <button
              onClick={() => setSyncNotice(null)}
              className="shrink-0 text-cyan-300 hover:text-white text-sm font-semibold px-2 py-1 rounded-lg hover:bg-cyan-500/20 transition-colors"
              aria-label="Dismiss notice"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Saving Indicator */}
      {isSaving && (
        <div className="fixed bottom-4 right-4 bg-gray-800 border-2 border-cyan-500/30 rounded-xl px-4 py-2 shadow-lg">
          <p className="text-cyan-300 flex items-center gap-2">
            <span className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-cyan-400"></span>
            Saving...
          </p>
        </div>
      )}

      {!isLoading && (
        <main className="flex-grow max-w-7xl w-full mx-auto px-3 sm:px-4 md:px-16 lg:px-20 py-4 sm:py-6 md:py-8">
          {/* Content column (centered; symmetric side padding leaves room for the rail on either side) */}
          <div className="min-w-0">
            {focusMode && (
              <FocusBar
                todayStatus={todayStatus}
                onMarkToday={() => todayEntry && toggleDay(todayEntry.date)}
                onExit={() => setFocusMode(false)}
              />
            )}
            <StreakBanner
              active={
                todayStatus === "none" &&
                (currentStreak > 0 || completedCount > 0)
              }
              reminderHour={streakReminderHour}
              todayKey={todayEntry ? todayEntry.date : formatDate(new Date())}
              onMarkToday={() => todayEntry && toggleDay(todayEntry.date)}
            />
            {isMonday && (
              <WeeklyReview
                variant="card"
                stats={weeklyStats}
                onOpen={() => setWeeklyOpen(true)}
              />
            )}
            {/* Time-aware greeting + motivational message */}
            <div data-panel-section>
              <Greeting
                userName={settings.userName}
                completed={completedCount}
                total={totalDays}
              />
            </div>

            {/* Important Tasks */}
            <div
              data-panel-section
              data-panel-key="important"
              data-panel-label="Important"
            >
              <ImportantTasks
                tabs={tabs}
                subGoals={subGoals}
                onToggleGoal={toggleGoal}
                onToggleSubGoal={toggleSubGoal}
              />
            </div>

            {/* Reminders */}
            <div
              data-panel-section
              data-panel-key="reminders"
              data-panel-label="Reminders"
              className="mb-6 sm:mb-8"
            >
              <CollapsiblePanel
                panelKey="reminders"
                onAi={aiPanelHandler}
                aiName={aiName}
                title="Reminders"
                icon={IcBell}
                collapsed={isCollapsed("reminders")}
                onToggle={() => togglePanelCollapsed("reminders")}
              >
                <Reminders
                  tabs={tabs}
                  subGoals={subGoals}
                  quickLinkItems={quickLinkReminderItems}
                  recurringItems={recurringReminderItems}
                  rememberItems={rememberReminderItems}
                  conferenceItems={conferenceReminders}
                  publicationItems={publicationReminders}
                  stickyNoteItems={stickyNoteReminders}
                  onToggleGoal={requestToggleGoal}
                  onToggleSubGoal={requestToggleSubGoal}
                  onToggleQuickLink={toggleQuickLinkCard}
                  onToggleRecurringOccurrence={toggleRecurringOccurrence}
                  onToggleRemember={toggleRememberItem}
                  onToggleConference={markConferenceSubmitted}
                  onTogglePublication={dismissPublicationReminder}
                  onToggleStickyNote={dismissStickyNoteReminder}
                  onUpdateGoalDeadline={updateGoalDeadline}
                  onUpdateSubGoalDeadline={updateSubGoalDeadline}
                  onUpdateQuickLinkDueDate={updateQuickLinkCardDueDate}
                  onUpdateRememberDueDate={updateRememberItemDueDate}
                />
              </CollapsiblePanel>
            </div>

            {/* Goals */}
            <div
              data-panel-section
              data-panel-key="goals"
              data-panel-label="Goals"
              className="mb-6 sm:mb-8"
            >
              <CollapsiblePanel
                panelKey="goals"
                onAi={aiPanelHandler}
                aiName={aiName}
                title="Goals"
                icon={IcTarget}
                count={tabs.reduce((n, t) => n + (t.goals || []).length, 0)}
                collapsed={isCollapsed("goals")}
                onToggle={() => togglePanelCollapsed("goals")}
              >
                <GoalsList
                  tabs={tabs}
                  activeTabId={activeTabId}
                  subGoals={subGoals}
                  onTabChange={setActiveTabId}
                  onAddTab={addTab}
                  onDeleteTab={deleteTab}
                  onRenameTab={renameTab}
                  onReorderTabs={reorderTabs}
                  onAddGoal={addGoal}
                  onAddGoalsBulk={addGoalsBulk}
                  onToggleGoal={requestToggleGoal}
                  onToggleGoalImportance={toggleGoalImportance}
                  onDeleteGoal={deleteGoal}
                  onEditGoal={editGoal}
                  onMoveGoalUp={moveGoalUp}
                  onMoveGoalDown={moveGoalDown}
                  onUpdateGoalDeadline={updateGoalDeadline}
                  onMoveGoalWithinTab={moveGoalWithinTab}
                  onMoveGoalBetweenTabs={moveGoalBetweenTabs}
                  onAddSubGoal={addSubGoal}
                  onToggleSubGoal={requestToggleSubGoal}
                  onDeleteSubGoal={deleteSubGoal}
                  onEditSubGoal={editSubGoal}
                  onUpdateSubGoalDeadline={updateSubGoalDeadline}
                  onToggleSubGoalImportance={toggleSubGoalImportance}
                  getSubGoalsForGoal={getSubGoalsForGoal}
                  areAllSubGoalsCompleted={areAllSubGoalsCompleted}
                />
              </CollapsiblePanel>
            </div>

            {/* Movable panels: Remember / Clipboard / Quick Links (user reorderable) */}
            {normalizePanelOrder(
              panelOrder,
              showConferences,
              showPublications,
            ).map((panelKey, idx, arr) => {
              const moveProps = {
                onMovePanelUp: () => movePanel(panelKey, "up"),
                onMovePanelDown: () => movePanel(panelKey, "down"),
                isFirstPanel: idx === 0,
                isLastPanel: idx === arr.length - 1,
              };

              if (panelKey === "stickyNotes") {
                return (
                  <div
                    key="stickyNotes"
                    data-panel-section
                    data-panel-key="stickyNotes"
                    data-panel-label="Sticky Notes"
                    className="hidden md:block mb-6 sm:mb-8"
                  >
                    <CollapsiblePanel
                      panelKey="stickyNotes"
                      onAi={aiPanelHandler}
                      aiName={aiName}
                      title="Sticky Notes"
                      icon={IcStickyNote}
                      count={boardNotes.length}
                      collapsed={isCollapsed("stickyNotes")}
                      onToggle={() => togglePanelCollapsed("stickyNotes")}
                    >
                      <StickyNotesBoard
                        count={boardNotes.length}
                        rows={boardRows}
                        cardH={NOTE_CARD_H}
                        gap={NOTE_GAP}
                        canRestore={canRestoreNotes}
                        onRestore={restoreStickyNotesLayout}
                        {...moveProps}
                      />
                    </CollapsiblePanel>
                  </div>
                );
              }

              if (panelKey === "remember") {
                return (
                  <div
                    key="remember"
                    data-panel-section
                    data-panel-key="remember"
                    data-panel-label="Remember"
                    className="mb-6 sm:mb-8"
                  >
                    <Remember
                      items={rememberItems}
                      quickLinkItems={quickLinkRememberItems}
                      conferenceItems={conferenceRemembers}
                      showInReminders={showRememberInReminders}
                      onToggleShowInReminders={setShowRememberInReminders}
                      onAddItem={addRememberItem}
                      onToggleItem={toggleRememberItem}
                      onEditItem={editRememberItem}
                      onDeleteItem={deleteRememberItem}
                      onUpdateDueDate={updateRememberItemDueDate}
                      onToggleQuickLink={toggleQuickLinkCard}
                      onToggleRecurringOccurrence={toggleRecurringOccurrence}
                      onCompleteRecurringSeries={completeRecurringSeries}
                      onUpdateRecurrence={updateRememberRecurrence}
                      onCompleteConferenceRemember={completeConferenceRemember}
                      {...moveProps}
                    />
                  </div>
                );
              }

              if (panelKey === "clipboard") {
                return (
                  <div
                    key="clipboard"
                    data-panel-section
                    data-panel-key="clipboard"
                    data-panel-label="Clipboard"
                    className="mb-6 sm:mb-8"
                  >
                    <CollapsiblePanel
                      panelKey="clipboard"
                      onAi={aiPanelHandler}
                      aiName={aiName}
                      title="Clipboard"
                      icon={IcClipboard}
                      count={clipboardCards.length}
                      collapsed={isCollapsed("clipboard")}
                      onToggle={() => togglePanelCollapsed("clipboard")}
                    >
                      <Clipboard
                        cards={clipboardCards}
                        onAddCard={addClipboardCard}
                        onUpdateCard={updateClipboardCard}
                        onDeleteCard={deleteClipboardCard}
                        onDeleteAllCards={deleteAllClipboardCards}
                        onReorderCards={reorderClipboardCards}
                        {...moveProps}
                      />
                    </CollapsiblePanel>
                  </div>
                );
              }

              if (panelKey === "quickLinks") {
                return (
                  <div
                    key="quickLinks"
                    data-panel-section
                    data-panel-key="quickLinks"
                    data-panel-label="Quick Links"
                    className="mb-6 sm:mb-8"
                  >
                    <QuickLinks
                      cards={quickLinkCards}
                      onAddCard={addQuickLinkCard}
                      onUpdateCard={updateQuickLinkCard}
                      onDeleteCard={deleteQuickLinkCard}
                      onDeleteAllCards={deleteAllQuickLinkCards}
                      onToggleLink={toggleQuickLinkLink}
                      {...moveProps}
                    />
                  </div>
                );
              }

              if (panelKey === "publications" && showPublications) {
                return (
                  <div
                    key="publications"
                    data-panel-section
                    data-panel-key="publications"
                    data-panel-label="Publications"
                    className="mb-6 sm:mb-8"
                  >
                    <CollapsiblePanel
                      panelKey="publications"
                      onAi={aiPanelHandler}
                      aiName={aiName}
                      title="Publications"
                      icon={IcBookOpen}
                      count={publications.length}
                      collapsed={isCollapsed("publications")}
                      onToggle={() => togglePanelCollapsed("publications")}
                    >
                      <Publications
                        publications={publications}
                        sortMode={publicationSortMode}
                        onSortModeChange={setPublicationSortMode}
                        onAddPublication={addPublication}
                        onUpdatePublication={updatePublication}
                        onDeletePublication={deletePublication}
                        {...moveProps}
                      />
                    </CollapsiblePanel>
                  </div>
                );
              }

              if (panelKey === "conferences" && showConferences) {
                return (
                  <div
                    key="conferences"
                    data-panel-section
                    data-panel-key="conferences"
                    data-panel-label="Conferences"
                    className="mb-6 sm:mb-8"
                  >
                    <CollapsiblePanel
                      panelKey="conferences"
                      onAi={aiPanelHandler}
                      aiName={aiName}
                      title="Conferences"
                      icon={IcGraduationCap}
                      count={conferenceCards.length}
                      collapsed={isCollapsed("conferences")}
                      onToggle={() => togglePanelCollapsed("conferences")}
                    >
                      <Conferences
                        cards={conferenceCards}
                        sortMode={conferenceSortMode}
                        onSortModeChange={setConferenceSortMode}
                        onAddCard={addConferenceCard}
                        onUpdateCard={updateConferenceCard}
                        onDeleteCard={deleteConferenceCard}
                        onDeleteAllCards={deleteAllConferenceCards}
                        {...moveProps}
                      />
                    </CollapsiblePanel>
                  </div>
                );
              }

              return null;
            })}

            {/* Sticky notes as a list (mobile only; desktop uses the floating layer) */}
            <StickyNotesPanel
              notes={stickyNotes}
              onAdd={addStickyNote}
              onUpdate={updateStickyNote}
              onDelete={deleteStickyNote}
            />

            {/* Challenge calendar (minimizable; collapsed = this week only) */}
            <CalendarPanel
              weeks={weeks}
              completedDays={completedDays}
              partialDays={partialDays}
              onToggleDay={toggleDay}
              collapsed={calendarCollapsed}
              onToggleCollapsed={() => setCalendarCollapsed((v) => !v)}
            />

            {/* Legend (only meaningful with the full grid) */}
            {!calendarCollapsed && (
              <div
                data-panel-section
                className="mt-8 bg-gray-800 rounded-3xl p-6 border-2 border-yellow-500/30"
              >
                <h3 className="text-lg font-bold text-yellow-400 mb-4">
                  Legend
                </h3>
                <p className="text-xs text-cyan-300/70 mb-3">
                  Click today to cycle:{" "}
                  <span className="text-cyan-200">not done</span> →{" "}
                  <span className="text-green-300">complete</span> →{" "}
                  <span className="text-yellow-300">partial</span> →{" "}
                  <span className="text-cyan-200">not done</span>
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-500 rounded-2xl border-2 border-green-300"></div>
                    <span className="text-cyan-200">Today (Done)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl border-2 border-yellow-300"></div>
                    <span className="text-cyan-200">Today (Partial)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-200 rounded-2xl border-2 border-green-300"></div>
                    <span className="text-cyan-200">Past Day (Done)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-yellow-200 rounded-2xl border-2 border-yellow-300"></div>
                    <span className="text-cyan-200">Past Day (Partial)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-red-200 rounded-2xl border-2 border-red-300"></div>
                    <span className="text-cyan-200">Past Day (Missed)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-700 rounded-2xl border-2 border-gray-600 opacity-50"></div>
                    <span className="text-cyan-200">Future Day (Locked)</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      )}

      <Footer
        onOpenPrivacy={() => openLegal("privacy")}
        onOpenTerms={() => openLegal("terms")}
        onOpenCookieSettings={openCookieSettings}
        onOpenHelp={() => setShowHelp(true)}
      />

      <HelpModal open={showHelp} onClose={() => setShowHelp(false)} />

      {/* AI companion (cloud accounts): wandering sprite + chat */}
      {!isLoading && !showLogin && useGoogleSheets && (
        <AiCompanion
          name={aiName}
          awake={aiAwake}
          onSleep={toggleAiAwake}
          configured={aiConfigured}
          settings={aiSettings}
          getState={getAiState}
          focus={aiFocus}
          onClearFocus={() => setAiFocus(null)}
          onApplyAction={applyAiAction}
          onOpenSettings={() => setShowSettings(true)}
          userName={settings.userName}
          openSignal={aiOpenSignal}
        />
      )}

      {/* Left-side panel rail (scroll-spy; mobile jump sheet) */}
      {!isLoading && !showLogin && <PanelRail side={railSide} />}

      {/* Floating sticky notes (desktop) + bottom-left add/hide controls */}
      {!isLoading && !showLogin && (
        <StickyNotesLayer
          notes={stickyNotes}
          hidden={notesHidden}
          onToggleHidden={setNotesHidden}
          onAdd={addStickyNote}
          onUpdate={updateStickyNote}
          onDelete={deleteStickyNote}
          canGather={canGatherNotes}
          canRestore={canRestoreNotes}
          onGather={gatherStickyNotes}
          onRestore={restoreStickyNotesLayout}
          onSendToBoard={sendStickyNoteToBoard}
          onReturnFromBoard={returnStickyNoteFromBoard}
          hiddenAnchors={collapsedPanels}
        />
      )}

      {/* Command palette, undo, celebration, pomodoro, weekly review (modal) */}
      {!isLoading && !showLogin && (
        <>
          <CommandPalette
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
            actions={paletteOpen ? buildPaletteActions() : []}
          />
          <UndoToast
            item={undoItem}
            onUndo={runUndo}
            onDismiss={() => setUndoItem(null)}
          />
          <Celebration burst={celebration} />
          <Pomodoro
            onFocusComplete={() =>
              celebrate("Focus session complete — take a break ☕")
            }
          />
          <WeeklyReview
            variant="modal"
            open={weeklyOpen}
            onClose={() => setWeeklyOpen(false)}
            stats={weeklyStats}
          />
        </>
      )}

      {/* Install as PWA prompt (only shows when the browser offers it) */}
      <InstallPWA />

      {/* Privacy / Terms modal */}
      <LegalModal
        isOpen={legalModal.open}
        type={legalModal.type}
        onClose={closeLegal}
      />

      {/* Cookie consent banner: only when analytics is available and no choice made */}
      {analyticsAvailable() && cookieConsent === null && (
        <CookieConsent
          onAccept={acceptCookies}
          onDecline={declineCookies}
          onOpenPrivacy={() => openLegal("privacy")}
          onOpenTerms={() => openLegal("terms")}
        />
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSave={saveSettings}
        onReset={resetAllData}
        onExport={exportData}
        onImport={importData}
        onLogout={handleLogout}
        onDeleteAllClipboards={deleteAllClipboardCards}
        onCleanupDuplicates={useGoogleSheets ? handleCleanupDuplicates : null}
        clipboardCount={clipboardCards.length}
        showConferences={showConferences}
        onToggleConferences={toggleShowConferences}
        showPublications={showPublications}
        onTogglePublications={toggleShowPublications}
        cloudEnabled={useGoogleSheets}
        onAfterRestore={handleAfterRestore}
        onFlushPending={doFlush}
        onAfterSectionRestore={() => syncPull({ full: true })}
        onExportMarkdown={exportMarkdown}
        onExportCsv={exportCsv}
        onShareCard={shareProgressCard}
        streakReminderHour={streakReminderHour}
        onStreakReminderHourChange={setStreakReminderHour}
        railSide={railSide}
        onRailSideChange={(side) =>
          setRailSide(side === "right" ? "right" : "left")
        }
        onAiSettingsChanged={setAiSettings}
      />

      {/* Sync Status Indicator - Floating */}
      {!showLogin && useGoogleSheets && (
        <SyncStatusIndicator
          isOnline={isOnline}
          isSynced={isSynced}
          isSyncing={isSaving}
          lastSyncTime={lastSyncTime}
          onManualSync={handleManualSync}
          hasUnsyncedChanges={hasUnsyncedChanges}
          storageMode={storageMode}
          error={error}
        />
      )}

      {/* Conflict Resolution Modal */}
      <ConflictResolutionModal
        isOpen={showConflictModal}
        onClose={() => {
          setShowConflictModal(false);
          setConflictData(null);
        }}
        differences={conflictData}
        onResolve={handleConflictResolution}
        localData={{
          settings,
          completedDays: Array.from(completedDays),
          partialDays: Array.from(partialDays),
          tabs,
          activeTabId,
          subGoals,
          clipboardCards,
          quickLinkCards,
          conferenceCards,
          showConferences,
          conferenceSortMode,
          publications,
          showPublications,
          publicationSortMode,
          rememberItems,
          showRememberInReminders,
          panelOrder,
          lastModified,
        }}
        cloudData={cloudData}
      />

      {/* Add-to-Remember Prompt */}
      <RememberPromptModal
        isOpen={rememberPrompt.open}
        itemName={rememberPrompt.name}
        onConfirm={confirmRememberPrompt}
        onDecline={declineRememberPrompt}
        onClose={closeRememberPrompt}
      />
    </div>
  );
}

export default App;
